// installSimoneAiTransport(el) — wires simone's chat surface (mounted
// at the workspace shell root via the Chat component) to dispatch
// through sdk.ai instead of the chrome-extension bridge.
//
// Simone's sendPrompt function snapshots the user message into
// `s.root.chatMessages`, flips `chatTyping: true`, and calls
// `window.__simoneSendOverBridge({ type: 'user_message', text, ... })`.
// In the extension that handle posts to a WebSocket (claude-code bridge)
// and the bridge emits assistant_delta / turn_complete events back to
// the page, which transport/ws.js stitches into chatMessages.
//
// Workspace has no such bridge — sdk.ai does the round-trip itself. We
// install a function that mirrors the bridge protocol enough to keep
// simone's UI happy: on each turn we
//
//   1. Generate an assistant id and push a placeholder
//      { id, role: 'assistant', text: '', streaming: true } row so the
//      Working indicator + the bubble-empty Dots both render.
//   2. Call sdk.ai.dispatch(text). For 'simone'/'providers' modes that
//      streams through /ai-chat/stream (text deltas); 'local' opens
//      ws://127.0.0.1:8765.
//   3. On each onChunk(delta), append to the assistant message's text
//      and re-push the array so DOMQL re-renders.
//   4. On onDone(result), flip streaming:false + chatTyping:false. If
//      the result is an action intent the SDK already populated
//      _aiActionProposals — AiActionConfirmCard handles the rest.
//
// Bridge status is forced to 'open' as long as the AI service is
// resolved — simone's composer disables the Send button when
// bridgeStatus !== 'open', so we need to look healthy.

import _wsShared from '@symbo-ls/workspace-shared'
import { shellGetAiService } from './shellSharedDelegates.js'
const shellUserFacingErrorMessage = (...args) => _wsShared.functions.userFacingErrorMessage(...args)
// EntityRef string form — the dedupe key for the merged `attached.refs` below.
const formatEntityRef = (...args) => _wsShared.functions.formatEntityRef(...args)
import { activeWorkspaceId } from './scopedClient.js'
import { buildPageContext } from './pageContext.js'
import { readAiAttached } from './aiAttached.js'
import { buildUiCatalog } from './uiCatalog.js'
import { readOnlyCaller, readOnlyCallOf, withReadOnlyOutcome } from './moduleReadOnlyCall.js'
import { createDirectWsPort, handleBridgePayload, handleAgentEvent } from './_simone/wsBridge.js'
import { toolOutcome, toolFailure } from './_simone/toolOutcome.js'
import { toolEmbedCard } from './_simone/toolEmbed.js'
import { repeatsNote } from './_simone/callNote.js'
import { stripAnswerTrailers } from './_simone/answerTrailers.js'
import { sanitizeAssistantProtocolText } from './_simone/protocolText.js'
import {
  normalizePendingClientCall,
  resumeFromPendingClientCall,
  turnSettledAfter
} from './_simone/pendingClientCall.js'
import { _toChatMessages, _continueWord } from './_simone/sessions.js'

let _shellInstalled = false
let _installerEl = null

// THE TURN IN FLIGHT, per chat scope ('general' | 'addapp' | 'dashboard'):
// what assistantTurnStop ends on screen, and what a follow-up waits for. A
// stopped turn keeps running on the SERVER — a workspace conversation has no
// cancel route, and its turn lock answers 409 ("already has a turn in
// progress") to a new message until the loop ends; the SDK swallows that 409,
// so a follow-up sent at once was never answered. The follow-up therefore
// waits for the stopped turn to settle (its onDone / onError) plus a short
// grace for the lock, at most STOPPED_TURN_WAIT_MS; only the latest waiting
// send of a scope goes.
const _turnsInFlight = new Map()
const _queuedSends = new Map()
// Both are keyed by the WORKSPACE and the scope: a stopped turn of one
// workspace never holds back a send of the next after a switch
// (moduleMemoCensus — tenant-keyed); a turn's entry leaves when it settles.
const _turnKey = (root, scope) => {
  const ws = root && root.workspace
  const id = ws && (ws.id || ws._id)
  return String(id || '') + '::' + scope
}
const STOPPED_TURN_WAIT_MS = 45000
const STOPPED_TURN_GRACE_MS = 600

// A bundle sentence for a line the PERSON reads in the thread (the failed-turn
// bubble and the three system lines: local bridge down, AI not ready, no active
// workspace): through the live shell element's polyglot, so it follows the
// session language, or the shipped English when no element is up — never a raw
// key.
// Everything else this file writes is aimed at the MODEL (tool results, the
// dispatched prompt's prefixes) or is a log line, and stays English on purpose.
const _sayKey = (key) => {
  const el = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
  return el && typeof el.call === 'function'
    ? String(el.call('polyglot', key))
    : String(_wsShared.functions.i18nStaticEnglish()[key] || key)
}

// The shell's own words for a message the server REFUSED FOR CREDITS — a
// registered step (functions/creditsRefusal.js), reached through the live shell
// element the way `_sayKey` reaches polyglot: the copy follows the session
// language, the way out follows the viewer's role. `_creditsRow` answers the
// row fields (the sentence and the metadata carrying the figures) for ONE
// message, or null for any other message; `_creditsRows` does the same over a
// list of chat rows. Every path that turns a server answer into a bubble asks
// one of them, so the server's prose cannot reach a bubble by a side door. With
// no element up (or a step that fails) the row stays as it came.
const _creditsEl = () => (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
const _creditsRow = (text, metadata, root, from) => {
  const el = from || _creditsEl()
  try {
    const worded =
      el && typeof el.call === 'function'
        ? el.call('creditsRefusalRow', text, metadata, root)
        : null
    return worded && typeof worded.text === 'string' ? worded : null
  } catch (_) {
    return null
  }
}
const _creditsRows = (rows, root, from) => {
  const el = from || _creditsEl()
  try {
    const worded =
      el && typeof el.call === 'function' ? el.call('creditsRefusalRows', rows, root) : null
    return Array.isArray(worded) ? worded : rows
  } catch (_) {
    return rows
  }
}

// Resolve the LIVE root state. The shell/auth boot can remount the tree, so a
// root captured once at install goes stale — root.update on a destroyed tree
// throws into the SDK's safe() callback swallow, and answers never paint
// (empty assistant bubbles) even though the server answers fine. Preference
// order: the root the SENDER passed alongside the envelope (sendPrompt
// resolves `this.state.root` live at send time — its user bubbles always
// render, so that root is proven-live), then the latest installer element's
// root.
// ONE SHELL ASSISTANT (Nika, 2026-10-03): the site builder on screen (the
// /preview route or a pane showing it) rides the turn as
// `context.previewProject` — `{ owner, key, projectId?, env?, page? }`,
// resolved PER SEND from the visible surface by the preview package's
// registered resolver (preview/functions/previewSite.js previewSiteInView),
// so a closed pane or another site's address never leaks into a turn.
const _shellPreviewProjectInView = (root) => {
  const siteEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
  const site =
    siteEl && typeof siteEl.call === 'function' ? siteEl.call('previewSiteInView', root) : null
  return site && typeof site === 'object' && site.owner && site.key ? site : null
}

const _shellResolveRoot = (extra) => {
  if (extra && extra.stateRoot && typeof extra.stateRoot.update === 'function') {
    return extra.stateRoot
  }
  const el = _installerEl
  return (
    (el && el.state && el.state.root) ||
    (el && typeof el.getRoot === 'function' && el.getRoot()?.state?.root) ||
    null
  )
}

// STOP — a chat scope's turn ends on screen where it is (Nika 2026-10-23: "fly
// voice and ai should stop when I talk, add interrupting"). The voice talk
// loop calls it when the person speaks over a reply still being written
// (shared components/VoiceOverlay.js, the barge-in). The reply keeps what
// arrived and says it was stopped, chatTyping clears, and nothing later of
// that turn lands. The conversation route has no cancel, so the server ends
// the turn unseen: its client tool calls are refused (the loop finishes
// sooner) and the next send of the scope waits for it (_turnsInFlight).
// `scope`: 'general' | 'addapp' | 'dashboard'. Returns whether a turn stopped
// — false when none is in flight, or it already ended.
export function assistantTurnStop(scope) {
  const s = scope === 'addapp' ? 'addapp' : scope === 'dashboard' ? 'dashboard' : 'general'
  const turn = _turnsInFlight.get(_turnKey(_shellResolveRoot(), s))
  return !!(turn && typeof turn.stop === 'function' && turn.stop())
}

export function installSimoneAiTransport(el) {
  if (typeof window === 'undefined') return
  // Refresh the element handle on EVERY call so re-installs (drawer remounts)
  // point at the current tree; only the window handler installs once.
  _installerEl = el
  const initRoot = _shellResolveRoot(null)
  if (initRoot?.update) initRoot.update({ bridgeStatus: 'open', agent: 'sdk-ai' })
  // What this shell renders (functions/assistantRecords.js
  // assistantClientCapabilities): the shell's SDK answers every method with a
  // promise, so the first read starts HERE and keeps its answer on the root;
  // a turn then reads it at once. A host without the function answers
  // undefined.
  try {
    if (el && typeof el.call === 'function') el.call('assistantClientCapabilities')
  } catch (_) {}
  // Reconcile the local-bridge lifecycle with the CURRENT target mode — if
  // the shell booted (or remounted) with 'local' already active, open the
  // socket now instead of waiting for the next TargetSwitcher click.
  try {
    syncSimoneLocalBridge.call(el)
  } catch (_) {}
  if (_shellInstalled) return
  _shellInstalled = true

  window.__simoneSendOverBridge = (envelope, extra) => {
    if (!envelope || !envelope.type) return
    const root = _shellResolveRoot(extra)

    // Local target → hand the envelope to the local simone-bridge VERBATIM
    // (same protocol standalone simone speaks). sdk.ai stays out of local
    // turns; bridge events stream back through syncSimoneLocalBridge's port
    // listener (handleBridgePayload stitches chatMessages). When the bridge
    // isn't up, fail honestly instead of swallowing the message.
    if (root && root.targetMode === 'local') {
      if (_localPort && root.bridgeStatus === 'open') {
        try {
          _localPort.postMessage({ type: 'send', payload: envelope })
        } catch (_) {}
      } else if (envelope.type === 'user_message') {
        _shellPushSystemMessage(
          root,
          _sayKey('workspace.simone.error.localBridge').replace('{url}', () => LOCAL_BRIDGE_URL)
        )
        root.update({ chatTyping: false })
      }
      return
    }

    if (envelope.type !== 'user_message') return
    const text = String(envelope.text || '').trim()
    if (!text) return
    // Scope: 'addapp' dispatches project-scoped to the active extension
    // (envelope.projectId / root._addAppProjectId); its state lives under _ax_*
    // so it never collides with the general drawer/composer.
    const scope =
      envelope.scope === 'addapp'
        ? 'addapp'
        : envelope.scope === 'dashboard'
          ? 'dashboard'
          : 'general'
    // 'dashboard' = the / inline-thread surface — workspace-scoped like 'general'
    // (the add-app-only branches below exclude it), just under _db_* keys.
    const k = scope === 'addapp' ? '_ax_' : scope === 'dashboard' ? '_db_' : ''
    const projectId =
      envelope.projectId || (scope === 'addapp' ? (root && root._addAppProjectId) || null : null)

    // A STOPPED turn of this scope still runs on the server (_turnsInFlight):
    // this send waits for it to settle, then goes — the newest waiting send
    // only. The user message is already on screen (sendPrompt).
    const turnKey = _turnKey(root, scope)
    const stoppedBefore = _turnsInFlight.get(turnKey)
    if (
      stoppedBefore &&
      stoppedBefore.stopped &&
      !stoppedBefore.settled &&
      !(extra && extra.afterStoppedTurn)
    ) {
      const ticket = {}
      _queuedSends.set(turnKey, ticket)
      const go = () => {
        if (_queuedSends.get(turnKey) !== ticket) return
        _queuedSends.delete(turnKey)
        window.__simoneSendOverBridge(envelope, {
          ...(extra || {}),
          stateRoot: root,
          afterStoppedTurn: true
        })
      }
      Promise.race([
        stoppedBefore.drain,
        new Promise((ok) => setTimeout(ok, STOPPED_TURN_WAIT_MS))
      ]).then(() => setTimeout(go, STOPPED_TURN_GRACE_MS))
      return
    }

    const ai = shellGetAiService()
    if (!ai) {
      _shellPushSystemMessage(root, _sayKey('workspace.simone.error.notReady'), k)
      if (root?.update) root.update({ [k + 'chatTyping']: false })
      return
    }

    // Scope sdk.ai to the active workspace so dispatch routes to
    // /core/agents/workspaces/:wsId/conversations (per-member history +
    // workspace actions). The shell holds the canonical workspace id in the
    // governance JWT claims (root.jwtClaims.active_workspace_id); the SDK
    // otherwise has no activeWorkspaceId and would error "no active workspace".
    try {
      const wsId = activeWorkspaceId(root)
      if (wsId && typeof ai.updateContext === 'function') {
        ai.updateContext({ activeWorkspaceId: wsId })
      } else if (!wsId && scope !== 'addapp') {
        _shellPushSystemMessage(root, _sayKey('workspace.simone.error.noWorkspace'), k)
        if (root?.update) root.update({ [k + 'chatTyping']: false })
        return
      }
    } catch (_) {
      /* best-effort — fall through to dispatch */
    }

    // A new user message CLOSES any still-parked approval — the server closes
    // the dangling client call before it starts this turn
    // (closeDanglingClientCall), so leaving Run / Skip live on the old row
    // would offer a button the loop can no longer honour. Settle first, so the
    // row goes quiet at the same moment the server writes its honest "NOT run".
    // This thread's own waiting steps are SUPERSEDED, never declined: nothing
    // is posted for them (a POST would race this message for the turn lock),
    // and the turn that waited on them ends here on screen — its stream would
    // otherwise answer this message's frames through the old callbacks.
    if (supersedeParkedAiToolApprovals(root, k, root && root[k + 'activeSessionId']) > 0) {
      const prior = _turnsInFlight.get(turnKey)
      if (prior && typeof prior.supersede === 'function') prior.supersede()
    }
    settleParkedAiToolApprovals(
      'A new message was sent before this action was approved — it did not run.'
    )

    // Per-turn guard: cleared at turn start, set when the cloud drives the
    // injectPageInApp client tool itself this turn. The onDone build-spec
    // render fallback reads it directly so it doesn't double-render when the
    // tool already ran.
    _extensionRenderedThisTurn = false

    const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const placeholder = {
      id: assistantId,
      role: 'assistant',
      text: '',
      ts: Date.now(),
      streaming: true,
      // Seed EVERY key a factory will later read reactively. A key that is
      // ABSENT at the row's first render is not tracked as a dependency, so
      // the factory never re-fires when the key later appears — the live
      // tool timeline rendered EMPTY (state carried the rows, DOM had none;
      // diagnosed live on dev 2026-08-12: Activity children:0 while
      // s.tools:2). Same latent trap for cards and suggestions.
      tools: [],
      cards: [],
      suggestions: [],
      // The author of THIS row, fixed at dispatch — the one instant at which
      // the thread's routing target and the message are the same fact. Null
      // means the general assistant wrote it, and it stays null forever.
      // AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1: ChatMessage's Author used to
      // fall back to the LIVE `root._targetAgentName`, so pressing "Talk to
      // QA" re-labelled every earlier reply in the open thread as QA
      // (measured on production 2026-08-30 06:43). The renderer now reads
      // this key only, which also makes it one of the keys that MUST be
      // seeded here — see the note above on absent keys never being tracked.
      agentName: root?._targetAgentName || null,
      // S1 (NIKA BATCH 2026-08-16) — null while streaming (ChatMessage shows
      // a live text-length estimate instead); onDone below patches in the
      // server's authoritative { promptTokens, completionTokens }. Seeded
      // here for the same reason as tools/cards/suggestions above.
      usage: null,
      // GROUNDING (spec §4.2–4.3) — the refs this answer cites and the
      // computed confidence verdict. Seeded here for exactly the reason
      // tools/cards/suggestions/usage are: a key ABSENT at the row's first
      // render is never tracked as a dependency, so ChatMessage's Sources /
      // Confidence factories would never re-fire when onDone patched them in.
      // `sources: []` renders nothing; `confidence: null` renders the muted
      // "Unscored" pill — never a hidden pill, because "the model did not say"
      // and "the model was confident" must not look the same (spec §4.3:
      // ALWAYS present on every assistant message).
      sources: [],
      confidence: null,
      // The advice level (vault spec 06 §7) — null unless the workspace
      // grades its answers. Seeded for the same dependency reason.
      level: null,
      // Touched once a second by the elapsed-timer interval below, purely to
      // give ChatMessage's live elapsed-seconds text a state key to react to
      // — same "seed every key a factory will read" rule as tools/cards/
      // suggestions/usage.
      _tick: Date.now()
    }
    if (root?.update) {
      root.update({
        [k + 'chatMessages']: (root[k + 'chatMessages'] || []).concat(placeholder),
        [k + 'currentAssistantId']: assistantId
      })
    }
    // This scope's turn in flight (_turnsInFlight): `stop` is set below, once
    // the stream's own buffers exist; `drain` resolves when the turn settles.
    let _drained = null
    const turn = {
      stopped: false,
      settled: false,
      stop: null,
      drain: new Promise((ok) => {
        _drained = ok
      })
    }
    const _settleTurn = () => {
      if (turn.settled) return
      turn.settled = true
      if (_turnsInFlight.get(turnKey) === turn) _turnsInFlight.delete(turnKey)
      _drained()
    }
    _turnsInFlight.set(turnKey, turn)

    // S1 (NIKA BATCH 2026-08-16) — elapsed-time ticker. ChatMessage's live
    // display has nothing to react to on pure wall-clock passage (no delta,
    // no tool event), so this nudges `chatMessages` once a second — the
    // same array-remap `_shellPatchAssistantMessage` already does per delta, just
    // on a timer instead of a stream event. Cleared on both onDone and
    // onError below; a stray interval outliving its message would tick a row
    // nobody is reading.
    let _elapsedTimer = setInterval(() => {
      _shellPatchAssistantMessage(root, assistantId, { _tick: Date.now() }, k)
    }, 1000)
    const _stopElapsedTimer = () => {
      if (_elapsedTimer) {
        clearInterval(_elapsedTimer)
        _elapsedTimer = null
      }
    }

    // Picked items are POINTERS in the workspace surface — the user aimed the
    // cursor at something to talk ABOUT it (vs the chrome extension, where a
    // pick marks the DOM node the agent should update). Compact one-liners
    // prefixed to the dispatched text so the agent always sees them.
    const pickedAll = Array.isArray(envelope.context?.pickedElements)
      ? envelope.context.pickedElements
      : []
    // An INSPECTED region (the composer's Inspect & ask, functions/
    // aiInspect.js) is a picked entry that carries its facts: it rides the
    // turn STRUCTURED as `context.inspected` (the server's INSPECTED line),
    // never as the 80-character pointing line below.
    const inspected = pickedAll
      .filter((p) => p && p.inspect && typeof p.inspect === 'object')
      .slice(0, 3)
      .map((p) => p.inspect)
    const picked = pickedAll.filter((p) => !(p && p.inspect && typeof p.inspect === 'object'))
    const pointing = picked.length
      ? picked
          .map((p) => {
            // The picked element's path (`Header › Nav › Link`) first — a bare
            // key names one of many same-named elements on a site.
            const path = Array.isArray(p?.keyPath) ? p.keyPath.join(' › ') : ''
            const name = path || p?.component || p?.key || p?.tag || 'element'
            const snippet = (p?.text || '').trim().slice(0, 80)
            return `[user is pointing at: ${name}${snippet ? ` — "${snippet}"` : ''}]`
          })
          .join('\n') + '\n\n'
      : ''

    // The builder bound to an app sends the person's words only. Which app the
    // thread changes is the SERVER's to say (CORE builder-r2: one per-turn line
    // names the thread's app and its edit path, update_app, for both app kinds;
    // get_project returns the pages). The turn's project route and
    // `context.extensionProjectId` already name it. The note this transport
    // used to prepend ("[editing multi-page app … do NOT create_app again.]")
    // was stored as the person's message and read back under "You" (sam,
    // round 1b); _simone/sessions.js still strips it from older threads.

    const dispatchContext = {
      surface: scope === 'addapp' ? 'add-app-builder' : 'workspace-shell',
      extensionProjectId: scope === 'addapp' ? projectId : null,
      pickedElements: picked.length ? picked : null,
      targetAgentId: root?._targetAgentId || null,
      targetAgentName: root?._targetAgentName || null
    }
    // UI LANGUAGE (server ai-boards/uiLanguage.js renders the USER-FACING
    // LANGUAGE instruction from it): the shell's active language pins every
    // model-authored user-facing string — ask_user / confirm_with_user
    // prompts and choice labels, narration, SUGGEST trailer — when the
    // message itself has no language signal (QA round-2 F9: an emoji-only
    // turn streamed a ka choice-pending row into an en workspace). Sent for
    // BOTH scopes — language is surface-independent.
    if (typeof root?.lang === 'string' && root.lang) {
      dispatchContext.uiLanguage = root.lang
    }
    // RESUME (the Continue chip, sendPrompt `opts.resume`): the turn picks up
    // a failed-after-tools turn. The message text is the member's own word;
    // the server reads `context.resume` and tells the model to continue
    // without redoing the saved actions. Sent for BOTH scopes.
    if (envelope.context && envelope.context.resume === true) {
      dispatchContext.resume = true
    }
    // MODE × PLACEMENT (AI_ASSISTANT_SPEC v1 §1–§2, §12.1): ONE brain answers
    // every ask and the MODE shapes its final output — 'assistant' (text + live
    // blocks), 'dashboard' (a board), 'tools' (work inside the platform apps),
    // 'apps' (a frontend app), 'presentation' (a deck), 'fly' (a composed page —
    // a chat turn in Fly mode answers in the page's shape). The PLACEMENT tells it how
    // wide the surface is: 'main' (the MainColumn thread, full-board-width
    // blocks), 'sidebar' (the docked panel) or 'minimized' (the compact
    // popover). The person's mode, read at send time (functions/
    // assistantMode.js); the AI may answer one ask in another mode and says so.
    // The add-app builder is extension-scoped and carries neither.
    if (scope !== 'addapp') {
      // ONE ask's own mode (the focus panel's Summarize / Build a board —
      // sendPrompt's `opts.mode`) wins for this turn; else the person's.
      const _askMode = envelope.context && envelope.context.assistantMode
      const _mode = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly'].includes(
        _askMode
      )
        ? _askMode
        : root?.assistantMode
      dispatchContext.assistantMode = [
        'assistant',
        'dashboard',
        'tools',
        'apps',
        'presentation',
        'fly'
      ].includes(_mode)
        ? _mode
        : 'assistant'
      dispatchContext.placement =
        root?._homeThreadOpen === true && root?.assistantDock === 'main'
          ? 'main'
          : root?.assistantDock === 'minimized'
            ? 'minimized'
            : 'sidebar'
      // WHAT THIS SHELL RENDERS (the server's capability gate, ai-boards
      // answerShape): `capabilities: { records: 1 }` only when the shell renders
      // RECORDS BLOCK v1 and its SDK resolves them (functions/assistantRecords.js
      // assistantClientCapabilities, first read at install); absent = the server
      // offers no embed_records. The SDK forwards `context` whole. An answer
      // still in flight (a promise, the first moments of a boot) sends nothing
      // this turn and lands on the root for the next. Best-effort — a host
      // without the function answers undefined, and a throw never blocks a turn.
      try {
        const capEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
        const caps =
          capEl && typeof capEl.call === 'function'
            ? capEl.call('assistantClientCapabilities')
            : null
        if (caps && typeof caps === 'object' && typeof caps.then !== 'function') {
          dispatchContext.capabilities = caps
        }
      } catch (_) {
        /* the capability is best-effort */
      }
      // THE BUILT-IN APPS' VIEWS AND FORMS (functions/assistantAppViews.js
      // assistantAppCatalog): calendar and tasks have no manifest the server
      // can read, so the turn carries their providers' declarations. An older
      // server ignores the key. Best-effort, like the capability.
      try {
        const catEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
        const catalog =
          catEl && typeof catEl.call === 'function' ? catEl.call('assistantAppCatalog') : null
        if (Array.isArray(catalog) && catalog.length) dispatchContext.appCatalog = catalog
      } catch (_) {
        /* the catalog is best-effort */
      }
    }
    // Page awareness: the GENERAL AI gets a compact snapshot of the current page
    // (route + its data + what it can do there) so it can answer about and act
    // on what the user is looking at. The add-app builder is extension-scoped
    // and deliberately gets none. Best-effort — never block a turn on it.
    if (scope !== 'addapp') {
      try {
        const pc = buildPageContext(root)
        if (pc && pc.currentPage) {
          dispatchContext.currentPage = pc.currentPage
          dispatchContext.pageData = pc.pageData || null
          dispatchContext.pageActions = pc.pageActions || null
          // What the user is focused on right now (open detail modal / editor /
          // dialog + the record it shows) — lets the AI resolve "this ticket /
          // doc / person / agent" against the exact thing on screen.
          dispatchContext.openView = pc.openView || null
          // Live UI + data-scope snapshot: which page/modal/slot/sidebar is
          // active + the workspace/org the SDK is pinned to, so the AI has full
          // situational context (see buildUiState / buildUiStateLine).
          dispatchContext.uiState = pc.uiState || null
          // What the member UNDID in this thread: the server says these items
          // are deleted, so the reply never claims them (_undoneInThread).
          const undone = _undoneInThread(root, k)
          if (undone.length) dispatchContext.uiState = { ...(pc.uiState || {}), undone }
        }
      } catch (_) {
        /* page awareness is best-effort */
      }
      // THE ASK SCOPE (functions/aiScope.js; the composer's context selector):
      // what this turn is ABOUT — the page, the app, the workspace, another
      // installed app, or the open record — with the app id, the route, the
      // page and the home widgets in it. The server renders it as the ASK
      // SCOPE line. Best-effort, like the capability above.
      try {
        const scopeEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
        const aiScope =
          scopeEl && typeof scopeEl.call === 'function'
            ? scopeEl.call('aiScopePayload', root)
            : null
        if (aiScope && typeof aiScope === 'object') dispatchContext.aiScope = aiScope
      } catch (_) {
        /* the scope is best-effort — the page awareness above still answers */
      }
      if (inspected.length) dispatchContext.inspected = inspected
      const previewProject = _shellPreviewProjectInView(root)
      if (previewProject) dispatchContext.previewProject = previewProject
      // Workspace knowledge layer: the active workspace's personalized surface
      // (module pages/components, live state keys + row shapes, skin vars) —
      // the server renders it as the WORKSPACE UI CATALOG instruction block so
      // answers reuse THIS workspace's UI (open_page_in_split its pages, extend
      // its components in render_answer_ui, read its loaded state).
      try {
        // The live design system the answer views mount under (the root
        // element's context) — its colour and theme names ride the catalog.
        const dsEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
        const catalog = buildUiCatalog(root, {
          designSystem: (dsEl && dsEl.context && dsEl.context.designSystem) || null
        })
        if (catalog) dispatchContext.uiCatalog = catalog
      } catch (_) {
        /* catalog is best-effort */
      }
    }

    // STRUCTURED ATTACHMENTS (spec §4.1) — `context.attached = { refs, canvas }`.
    // The server allowlist (AgentWorkspaceConversationController.appendMessage)
    // keeps this key after validation + caps; the orchestrator renders it as a
    // fenced ATTACHED CONTEXT block and registers every ref as a retrieval
    // candidate. That is what makes these `sent: 'structured'` in the
    // awareness bar, and the bar and this block MUST agree — a chip that
    // claimed 'structured' while the wire dropped the item is the exact drift
    // buildAiAwareness exists to prevent.
    //
    // TWO SOURCES, merged here and nowhere else:
    //   • `root.aiAttached` — the persistent bag the drawer chips render
    //     (attachAiRef / attachAiCanvas from a page or a composer drop).
    //   • `envelope.context.attached` — the PER-TURN payload the home/canvas
    //     composer forwards from getSelectionAiContext() (canvas lane). Passed
    //     through untouched: that lane owns its own node/connection caps and
    //     re-capping here would silently truncate its payload.
    //
    // Refs union, deduped by the `ref:<type>:<id>` string form, persistent bag
    // first (an explicit attach outranks a selection that happens to overlap).
    // Capped at 24 = the server's own cap, so the client never sends items the
    // wire will drop.
    //
    // The whole key is OMITTED when the turn carries nothing, so a plain
    // dispatch payload is byte-identical to what it was before attachments
    // existed — the same rule the `attachments` image leg follows below.
    // The add-app builder is extension-scoped and gets none, like page
    // awareness above.
    if (scope !== 'addapp') {
      try {
        const bag = readAiAttached(root)
        const turn =
          envelope.context && typeof envelope.context.attached === 'object'
            ? envelope.context.attached
            : null
        const turnRefs = turn && Array.isArray(turn.refs) ? turn.refs : []
        const seen = new Set()
        const refs = []
        for (const ref of [...bag.refs, ...turnRefs]) {
          const key = formatEntityRef(ref)
          if (!key || seen.has(key)) continue
          seen.add(key)
          refs.push(ref)
          if (refs.length >= 24) break
        }
        const canvas =
          bag.canvas ||
          (turn && turn.canvas && typeof turn.canvas === 'object' ? turn.canvas : null)
        if (refs.length || canvas) {
          dispatchContext.attached = { refs, ...(canvas ? { canvas } : {}) }
        }
      } catch (_) {
        /* attachments are best-effort — never block a turn on them */
      }
    }

    // Route this message to the user's ACTIVE thread (set by newSession /
    // switchSession, restoreActiveThread after a reload, or the transport itself
    // once a turn names its conversation). Real server ids only — a local 's-…'
    // placeholder names no server conversation yet.
    //
    // ONE BUILDER THREAD = ONE SERVER CONVERSATION (presets round 2, aiko-r2 +
    // marcus-r2): an unbound add-app thread (no app yet, projectId null) carries
    // its id like every other thread. It used to carry only the workspace setup's
    // first thread, and every other unbound builder turn went out with
    // `conversationId: null` — a follow-up or a retry after a failed first build
    // then landed in whatever conversation the SDK had cached for the workspace
    // (any scope's), or, with the placeholder still active, in a NEW thread that
    // is not the free first build's thread and pays.
    //
    // The old guard (05fcc8c7c) kept a leftover PROJECT thread id out of an
    // unbound turn. That case answers itself now: a project conversation is not
    // a workspace conversation, the server answers 404 on the workspace route,
    // and the SDK (then this transport's gone-thread retry below) starts a new
    // conversation with the same message — what the old `null` did.
    const activeConvId = (() => {
      const id = root[k + 'activeSessionId']
      return id && !String(id).startsWith('s-') ? id : null
    })()
    // A local 's-…' placeholder means the user explicitly opened a NEW thread
    // (newSession) that has no server conversation yet. Tell the SDK to CREATE
    // one instead of falling back to its cached last-used conversation — the
    // fallback silently appended the "new" thread onto the previous
    // conversation, so the model kept the old thread's full context (stale
    // answers in a visually-empty chat).
    const wantsNewConversation =
      !activeConvId && String(root[k + 'activeSessionId'] || '').startsWith('s-')

    let buffered = ''
    // Reasoning segmentation: the model narrates its thinking BETWEEN tool
    // calls ("Let me fetch the records first…"). Everything streamed since the
    // last tool becomes THAT tool's reasoning note when it starts — the
    // narration snaps out of the bubble into the timeline row, the bubble
    // resets for the next segment, and onDone's final text replaces it all.
    let narratedUpto = 0
    // Delta flush throttle (2026-08-01, per Nika "streaming animation /
    // texts flickering"): SSE frames can land many-per-frame; patching the
    // chatMessages array per delta made the bubble repaint in bursts (and
    // every patch is a whole-array state write). The FIRST token flushes
    // immediately — that's the paint that replaces the "Thinking…" loader —
    // then flushes coalesce to one per ~60ms. The flush closure re-reads
    // `buffered`/`narratedUpto` at fire time, so tool-segmentation snapshots
    // stay correct regardless of cadence. onDone/onError clear the pending
    // timer so a late flush can never overwrite the final answer.
    let _flushTimer = null
    let _firstTokenPainted = false
    const turnStartedAt = Date.now()
    const _flushStream = () => {
      _flushTimer = null
      _shellPatchAssistantMessage(
        root,
        assistantId,
        { text: _stripSuggestTail(buffered.slice(narratedUpto)) },
        k
      )
    }
    const _cancelPendingFlush = () => {
      if (_flushTimer) {
        clearTimeout(_flushTimer)
        _flushTimer = null
      }
    }
    // THE PERSON STOPS THE TURN (assistantTurnStop): the reply keeps what
    // arrived — the bubble's own segment, as _flushStream paints it — and says
    // it was stopped under it, the way a failed turn keeps its partial answer;
    // the composer is free again. Everything this turn sends later is ignored
    // on screen (the guards in the callbacks below).
    turn.stop = () => {
      if (turn.stopped || turn.settled) return false
      turn.stopped = true
      _cancelPendingFlush()
      _stopElapsedTimer()
      // An action still waiting on Run / Skip never runs for a stopped turn.
      settleParkedAiToolApprovals(
        'The person stopped this turn before this action was approved — it did not run.'
      )
      // A question still waiting on its answer closes: the loop reads that it
      // was not answered, ends, and frees the thread for the next message.
      _endWaitingCards(
        (w) => _waitsInThread(w, root, k, turn.thread),
        'stop',
        'The person stopped this turn before answering — the question was not answered.'
      )
      const said = _stripSuggestTail(buffered.slice(narratedUpto)).trim()
      const note = _sayKey('workspace.simone.turn.stopped')
      _shellPatchAssistantMessage(
        root,
        assistantId,
        { text: said ? `${said}\n\n> ${note}` : note, streaming: false, stopped: true },
        k
      )
      if (root?.update) {
        root.update({ [k + 'chatTyping']: false, [k + 'currentAssistantId']: null })
      }
      return true
    }
    // A NEW MESSAGE TOOK THE THREAD (supersedeParkedAiToolApprovals, at the
    // next send): this turn waited on a step the server now closes under that
    // message. Its bubble keeps what streamed and stops its dots; everything
    // its stream sends later lands nowhere (the guards below) — the new turn
    // owns the thread, its typing flag and its frames.
    turn.supersede = () => {
      if (turn.stopped || turn.settled || turn.superseded) return false
      turn.superseded = true
      _cancelPendingFlush()
      _stopElapsedTimer()
      _shellPatchAssistantMessage(
        root,
        assistantId,
        { text: _stripSuggestTail(buffered.slice(narratedUpto)).trim(), streaming: false },
        k
      )
      _settleTurn()
      return true
    }
    // SIMONE-INFLIGHT-TURN-NO-PERSISTENCE-1 — mark the remembered thread
    // in-flight for the length of this turn. A reload from here can then say
    // honestly that the turn died (and that anything parked on the approval
    // gate never ran) instead of returning an empty panel.
    try {
      const markEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
      if (markEl && typeof markEl.call === 'function') markEl.call('markThreadTurn', scope, true)
    } catch (_) {
      /* best-effort — persistence is never a blocker on sending */
    }
    // Image attachments the composer put on the envelope (sendPrompt →
    // ./_simone/attachments.js). Forwarded VERBATIM to sdk.ai, which posts
    // them on the turn's message; the server caps them and persists them as
    // `image` content parts the multimodal providers send as real image
    // blocks. Omitted when the turn has none, so a text-only dispatch payload
    // is byte-identical to what it was before attachments existed — that is
    // also what keeps the local-bridge branch above (which forwards the whole
    // envelope untouched) unaffected.
    const attachments = Array.isArray(envelope.attachments) ? envelope.attachments : []
    // A THREAD THAT IS GONE: the server answers 404 (the same body as a
    // missing thread) when a member posts into a thread they deleted, and the
    // SDK may still hold that id as its "last used" one. Answered ONCE, before
    // any word arrived (onError below): drop the thread id (a new local
    // thread), send the SAME payload again as a new conversation through the
    // SAME callbacks, into the same answer bubble — the user message is already
    // on screen and is not added again. A second 404, or any other error,
    // fails the turn as before.
    let _goneThreadRetried = false
    let _turnCallbacks = null
    const _turnPayload = {
      text: pointing + text,
      projectId: scope === 'addapp' ? projectId : null,
      conversationId: activeConvId,
      newConversation: wantsNewConversation,
      context: dispatchContext,
      ...(attachments.length ? { attachments } : {})
    }
    // The thread this turn belongs to, as the root names it: the id (or local
    // placeholder) it was sent from; moved when the turn learns its server
    // conversation. A conversation id the turn reports is adopted only while
    // the person is still on that thread — a thread they switched to meanwhile
    // is never repointed.
    turn.thread = root[k + 'activeSessionId'] || null
    const _adoptTurnConversation = (id) => {
      const conv = id ? String(id) : ''
      if (!conv || conv.startsWith('s-') || !root?.update) return false
      const cur = root[k + 'activeSessionId'] || null
      if (cur !== turn.thread && cur !== null) return false
      turn.thread = conv
      if (cur !== conv) root.update({ [k + 'activeSessionId']: conv })
      return true
    }
    // THE PAGE FOLLOWS THE TURN'S WRITES. Every tool-activity frame reaches the
    // write hooks (onToolEvent below): the add-app preview for the builder's
    // turn, else the site builder on screen and the open canvas editor
    // (preview/functions/previewSite.js previewAfterAiWrite records each
    // project write as `_aiProjectWrite`, canvas RemoteUpdateWatcher follows
    // it). A turn whose stream was lost replays the steps its SAVED transcript
    // holds through the same hooks (_followDetachedTurn / _reconcileAfterTurnError
    // `onSteps`) — dev 2026-10-30: the stream died in a build turn, the answer
    // came from the saved thread, and the open canvas never heard of the page
    // the turn added. Each call reaches the hooks completed once.
    const _hookedCalls = new Set()
    const _afterToolFrame = (evt) => {
      if (!evt || !evt.phase) return
      // A successful app write of the builder's turn: the preview reads its
      // rows again, and a write nothing renders re-renders the app
      // (functions/addAppPreviewRefresh.js — persona round 3, B5).
      if (scope === 'addapp' && evt.phase === 'completed' && evt.ok !== false) {
        try {
          const writeEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
          if (writeEl && typeof writeEl.call === 'function') {
            Promise.resolve(writeEl.call('addAppPreviewAfterWrite', evt.tool)).catch((err) =>
              console.warn('[simone] preview refresh failed:', err?.message || err)
            )
          }
        } catch (_) {
          /* the preview refresh never breaks a turn */
        }
      }
      // The site builder on screen follows its project's writes DURING the turn
      // (preview/functions/previewSite.js previewAfterAiWrite).
      if (scope !== 'addapp') {
        try {
          const siteEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
          if (siteEl && typeof siteEl.call === 'function') {
            siteEl.call('previewAfterAiWrite', evt, root)
          }
        } catch (_) {
          /* the builder refresh never breaks a turn */
        }
      }
      if (evt.phase === 'completed' && evt.callId != null) _hookedCalls.add(String(evt.callId))
    }
    const _replaySteps = (steps) => {
      if (turn.stopped || turn.superseded || !Array.isArray(steps)) return
      for (const step of steps) {
        if (!step || !step.callId || _hookedCalls.has(step.callId)) continue
        _afterToolFrame({
          phase: 'completed',
          callId: step.callId,
          tool: step.tool,
          args: step.args,
          ok: step.ok
        })
      }
    }
    // THE TURN FAILED — the server answered with an error, or a lost
    // connection found nothing to follow (onError, _followAfterDrop): the
    // honest sentence under whatever streamed, the composer free again, and a
    // short re-read in case the answer landed anyway.
    const _failTurn = (err) => {
      _stopElapsedTimer()
      // Raw error stays in the console for debugging — only the
      // user-facing bubble text is sanitized (never a URL / adblocker
      // diagnostic / stack, see userFacingErrorMessage).
      console.warn('[simone] turn failed:', err?.message || err)
      // A parked approval would strand the SERVER-side turn forever (the
      // loop waits on a tool-result this dead turn can no longer post) —
      // settle them so the loop finishes honestly instead of the user having
      // to type "continue": as steps that did not run (the person never
      // decided), with the way to run them again — never as their "skip".
      settleParkedAiToolApprovals('The turn failed before this action was approved.', {
        notRun: 'workspace.simone.approval.notRun'
      })
      // …and a question still waiting on its answer closes the same way.
      _endWaitingCards(
        (w) => _waitsInThread(w, root, k, turn.thread),
        'end',
        'The turn failed before the person answered — the question was not answered.'
      )
      // A FAILED TURN KEEPS ITS THREAD: the SDK names the conversation the turn
      // ran in on its error. While the person is still on this turn's thread
      // (its placeholder, or the id it was sent with), point the thread at it —
      // the retry after a failed build goes into the SAME conversation (the free
      // first build's), never a new one. Before the in-flight mark settles, so
      // the remembered thread is the real one too.
      _adoptTurnConversation(err && err.conversationId)
      try {
        const markEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
        if (markEl && typeof markEl.call === 'function') markEl.call('markThreadTurn', scope, false)
      } catch (_) {}
      // A TURN TO AN INACTIVE AGENT (CORE-2f; the server's targetAgentActive.js):
      // the server refuses it before it writes anything — 403, `code:
      // 'agent_inactive'` on the body, which the SDK hands on as `err.cause`. The
      // shell words it in the session language; its English `message` never
      // shows. Any other 403, and every other error, keeps the lines below.
      const agentInactive =
        !!err && err.status === 403 && !!err.cause && err.cause.code === 'agent_inactive'
      const friendly = agentInactive
        ? _sayKey('workspace.simone.error.agentInactive')
        : shellUserFacingErrorMessage(err, {
            networkMessage: _sayKey('workspace.simone.error.network'),
            streamMessage: _sayKey('workspace.simone.error.stream'),
            fallback: (e) => {
              const msg = (e?.message || String(e) || '').trim()
              return msg
                ? _sayKey('workspace.simone.error.failedWithMessage').replace(
                    '{message}',
                    () => msg
                  )
                : _sayKey('workspace.simone.error.failed')
            }
          })
      // NEVER destroy already-streamed words with the error string — keep
      // the partial answer and append the error under it.
      const partial = _stripSuggestTail(buffered)
      _shellPatchAssistantMessage(
        root,
        assistantId,
        {
          text: partial ? `${partial}\n\n> ${friendly}` : friendly,
          streaming: false,
          turnError: true
        },
        k
      )
      if (root?.update) root.update({ [k + 'chatTyping']: false, [k + 'currentAssistantId']: null })
      _settleTurn()
      // The failure is often CLIENT-side only: the server finished the
      // turn and persisted its answer while our connection died (proven
      // live 2026-08-11 — three "couldn't reach the server" bubbles whose
      // real answers sat completed in the conversation, unseen). Re-read
      // the conversation on a short backoff and adopt the newest
      // assistant text if one landed.
      _reconcileAfterTurnError({
        root,
        k,
        scope,
        assistantId,
        // The SDK resolves the real conversation id (cache/create) after
        // dispatch — on failure it rides the error, which is the only
        // reliable source when the local session was still a placeholder.
        conversationId: (err && err.conversationId) || activeConvId,
        ai,
        turnStartedAt,
        opts: _turnPayload.projectId ? { projectId: _turnPayload.projectId } : null,
        sentText: _turnPayload.text,
        onSteps: _replaySteps
      })
    }
    // THE CONNECTION DROPPED, THE TURN GOES ON (onError — sam, round 1b): the
    // bubble keeps what streamed and says the work goes on, a parked approval
    // stays live (the person may still answer it; the SDK posts the result),
    // and the conversation is followed until the answer lands
    // (_followDetachedTurn). `extra.detachedFollow` tunes the follow's timing
    // for a test; nothing else passes it.
    const _followAfterDrop = (err, convId) => {
      const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
      const said = () => _stripSuggestTail(buffered.slice(narratedUpto)).trim()
      const noted = (key) => {
        const words = said()
        const note = _sayKey(key)
        return words ? `${words}\n\n> ${note}` : `> ${note}`
      }
      if (root?.update) root.update({ [k + 'activeSessionId']: convId, [k + 'chatTyping']: true })
      _shellPatchAssistantMessage(
        root,
        assistantId,
        { text: noted('workspace.simone.turn.reconnecting'), streaming: true },
        k
      )
      const finish = () => {
        _stopElapsedTimer()
        try {
          if (callEl && typeof callEl.call === 'function') {
            callEl.call('markThreadTurn', scope, false)
            callEl.call('refreshSessionsAfterTurn', scope)
            if (scope === 'addapp') callEl.call('loadAddAppExtensions', root)
          }
        } catch (_) {
          /* best-effort — history refreshes on its next read */
        }
        if (root?.update)
          root.update({ [k + 'chatTyping']: false, [k + 'currentAssistantId']: null })
        _settleTurn()
      }
      const follow = () =>
        _followDetachedTurn({
          root,
          k,
          assistantId,
          conversationId: convId,
          ai,
          sentText: _turnPayload.text,
          opts: _turnPayload.projectId ? { projectId: _turnPayload.projectId } : null,
          timing: extra && extra.detachedFollow,
          onSteps: _replaySteps,
          onAnswer: (answer) => {
            if (turn.superseded) return
            // A refusal for credits reads as the shell's sentence here too.
            const refused = _creditsRow(answer.text, answer.metadata, root)
            const suggestions = Array.isArray(answer.metadata?.suggestions)
              ? answer.metadata.suggestions
              : []
            _shellPatchAssistantMessage(
              root,
              assistantId,
              {
                text: refused ? refused.text : stripAnswerTrailers(answer.text),
                ...(refused ? { metadata: refused.metadata } : {}),
                suggestions,
                streaming: false,
                turnError: false,
                recovered: true
              },
              k
            )
            finish()
          },
          onHandOver: (pending) => {
            if (turn.superseded) return
            // The server asked for a client step while the stream was down: the
            // reload path offers it (the gate asks again in Ask first) and
            // follows the rest; this bubble keeps only what streamed.
            _shellPatchAssistantMessage(root, assistantId, { text: said(), streaming: false }, k)
            finish()
            try {
              reofferPendingClientCall.call(callEl, {
                offer: pending,
                conversationId: convId,
                scope,
                stateRoot: root,
                ai,
                // the live turn's own step: the person is here, not a load
                live: true,
                opts: _turnPayload.projectId ? { projectId: _turnPayload.projectId } : undefined
              })
            } catch (_) {}
          },
          onWaiting: () => {
            if (turn.superseded) return
            // The follow window ended while a step waits for the person: the row
            // stays live, the bubble says so, and an answer to it follows again.
            _shellPatchAssistantMessage(
              root,
              assistantId,
              { text: noted('workspace.simone.turn.waitingForYou'), streaming: false },
              k
            )
            if (root?.update) root.update({ [k + 'chatTyping']: false })
            for (const id of Object.keys(_pendingToolApprovals)) {
              const held = _pendingToolApprovals[id]
              if (!held || held.root !== root || held.k !== k) continue
              held.onAnswered = () => {
                _shellPatchAssistantMessage(
                  root,
                  assistantId,
                  { text: noted('workspace.simone.turn.reconnecting'), streaming: true },
                  k
                )
                if (root?.update) root.update({ [k + 'chatTyping']: true })
                follow()
              }
            }
          },
          onGiveUp: () => {
            if (!turn.superseded) _failTurn(err)
          },
          onLost: () => {
            // The thread moved on: the bubble keeps what streamed and stops
            // its dots (a stop already wrote its own note), the turn settles.
            if (!turn.stopped && !turn.superseded) {
              _shellPatchAssistantMessage(root, assistantId, { text: said(), streaming: false }, k)
            }
            _stopElapsedTimer()
            _settleTurn()
          }
        })
      follow()
    }
    // A NEW THREAD GETS ITS SERVER CONVERSATION BEFORE ITS FIRST TURN (presets
    // round 2, aiko-r2 + marcus-r2). Left to the SDK, the thread is made with the
    // message and its id reaches this page only when the turn ENDS — a build
    // turn runs for minutes, so a message the person sent meanwhile (after a
    // choice card, a failed build step) still saw the 's-…' placeholder, went
    // out as `newConversation: true` and opened a SECOND thread: not the free
    // first build's thread, so it paid and was refused. `createConversation` is
    // the same POST the SDK makes lazily (same route: the turn's project or the
    // active workspace, empty body), only made first: the thread points at the
    // real id at once, and the in-flight mark is written again with it, so a
    // reload mid-turn restores this thread. No method on the SDK, or the create
    // fails: the SDK makes the thread with the message, as before.
    const _sendTurn = (payload, callbacks) => {
      if (!wantsNewConversation || typeof ai.createConversation !== 'function') {
        ai.dispatch(payload, callbacks)
        return
      }
      Promise.resolve()
        .then(() =>
          ai.createConversation(payload.projectId ? { projectId: payload.projectId } : {})
        )
        .then(
          (conv) => {
            const id = conv && (conv.id || conv._id)
            if (!id) return
            // the turn goes into the conversation made for it, even when the
            // person opened another thread meanwhile (that one is not repointed)
            payload.conversationId = String(id)
            payload.newConversation = false
            if (!_adoptTurnConversation(id)) return
            try {
              const markEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
              if (markEl && typeof markEl.call === 'function')
                markEl.call('markThreadTurn', scope, true)
            } catch (_) {
              /* best-effort — persistence is never a blocker on sending */
            }
          },
          (err) => {
            console.warn('[simone] the new thread was not made first:', err?.message || err)
          }
        )
        .then(() => ai.dispatch(payload, callbacks))
    }
    _sendTurn(
      _turnPayload,
      (_turnCallbacks = {
        onChunk: (delta) => {
          if (turn.stopped || turn.superseded) return
          buffered += delta
          if (!_firstTokenPainted) {
            _firstTokenPainted = true
            _flushStream()
            return
          }
          if (!_flushTimer) _flushTimer = setTimeout(_flushStream, 60)
        },
        onDone: (result) => {
          // A superseded turn's stream ends with the NEXT turn's answer — that
          // answer is the new turn's, through its own callbacks.
          if (turn.superseded) return
          _cancelPendingFlush()
          _stopElapsedTimer()
          _settleRetryingTools(root, assistantId, k)
          // A STOPPED turn the server finished anyway: keep the thread
          // continuable (a new thread's id) and the reply in the server's
          // id-space — never its answer, its hand-offs or its voice. Then the
          // follow-up that waits for it may go.
          if (turn.stopped) {
            if (root?.update) {
              const patch = {}
              if (result?.conversationId) patch[k + 'activeSessionId'] = result.conversationId
              if (result?.messageId) {
                patch[k + 'chatMessages'] = (root[k + 'chatMessages'] || []).map((m) =>
                  m && m.id === assistantId ? { ...m, id: String(result.messageId) } : m
                )
              }
              if (Object.keys(patch).length) root.update(patch)
            }
            try {
              const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
              if (callEl && typeof callEl.call === 'function') {
                callEl.call('markThreadTurn', scope, false)
                callEl.call('refreshSessionsAfterTurn', scope)
              }
            } catch (_) {
              /* best-effort — history refreshes on its next read */
            }
            _settleTurn()
            return
          }
          // The loop ended with a question of this turn still open: the server
          // closed its call (the SDK never ends a turn while a client call
          // runs), so the card closes too — nothing is posted for it.
          _endWaitingCards((w) => _waitsInThread(w, root, k, turn.thread), 'end', null)
          // The server's final answer is the post-tool summary. When it is
          // MISSING (provider produced no final text / the tail was lost),
          // fall back to the WHOLE buffered narration — never just the
          // post-last-tool slice. Earlier segments may also live in tool rows
          // as notes; that duplication is strictly better than what shipped:
          // slicing from `narratedUpto` dropped every word streamed before
          // the last tool call, so answers rendered starting mid-sentence
          // ("the Backlog column\n3. Open it…" — dev.my 2026-08-11).
          let finalText = stripAnswerTrailers(result?.text) || _stripSuggestTail(buffered) || ''
          // Follow-up suggestions (server-validated, from the assistant
          // message's metadata) ride the message row — ChatMessage renders
          // them as tappable pills under the newest reply.
          const suggestions = Array.isArray(result?.suggestions) ? result.suggestions : []
          // The assistant message's raw metadata (tickets/opus.md
          // "credits-exhausted UX") — `{ kind:'error', errorCode,
          // errorMessage, retryable, creditsExhausted }` when
          // failWorkspaceLoop synthesized this answer after a loop failure,
          // null on a normal answer. Rides the row exactly like
          // `suggestions` above so ChatMessage's onInit/onUpdate hook can key
          // off `s.metadata` to fire the role-aware notification/banner.
          let metadata = result?.metadata || null
          // AI-302 — tool-call protocol text must never render as the answer.
          // Post-AI-203 the server recovers these on live turns; this is the
          // state-layer backstop for provider paths that bypass the fixed
          // loop. The raw text survives in metadata for diagnosis.
          const leakCheck = sanitizeAssistantProtocolText(finalText)
          if (leakCheck.leaked) {
            finalText = leakCheck.text
            metadata = { ...(metadata || {}), protocolLeak: true, rawProtocolText: leakCheck.raw }
          }
          // S1 (NIKA BATCH 2026-08-16) — server's authoritative token count,
          // present on both the streaming and non-streaming AiChatService
          // response shapes (`{ promptTokens, completionTokens }`). null/{}
          // (a turn with no thread, or a provider that never reported usage)
          // both resolve to null so ChatMessage can tell "no data" from
          // "zero tokens" — see its own PvUsage hide: gate.
          const usage =
            result?.usage && typeof result.usage.completionTokens === 'number' ? result.usage : null
          // GROUNDING (spec §4.2–4.3). The server returns both on the
          // dispatch result; older/streaming shapes carry them on the
          // assistant message's metadata instead, so read the result first
          // and fall back to metadata — the SAME two-place read
          // sessions._toChatMessages does when it restores a reloaded thread,
          // so a live turn and a reloaded one render identically.
          //
          // `confidence` stays null when neither place has one; ChatMessage
          // renders the muted "Unscored" pill for that, never nothing.
          const sources = Array.isArray(result?.sources)
            ? result.sources
            : Array.isArray(metadata?.sources)
              ? metadata.sources
              : []
          const confidence =
            result?.confidence && typeof result.confidence === 'object'
              ? result.confidence
              : metadata?.confidence && typeof metadata.confidence === 'object'
                ? metadata.confidence
                : null
          // The same two-place read for the advice level (vault spec 06 §7).
          const level =
            result?.level && typeof result.level === 'object'
              ? result.level
              : metadata?.level && typeof metadata.level === 'object'
                ? metadata.level
                : null
          // The last call's words show ONCE. The words a model streams beside
          // a call (core-wl callText: saved on the tool-call message, server
          // 84527812) became that call's NOTE at its `started` frame. Two
          // cases put those same words in the answer too — show them as the
          // answer and clear the note, never both:
          //   · the final answer REPEATS the note (whitespace-normalized) —
          //     the model said it beside the call, then again as its answer,
          //     streamed after the call (approved (A), _simone/callNote.js);
          //   · the turn ENDS on its call (nothing streamed after it) and the
          //     server's final text is empty or the whole narration.
          let toolsPatch = null
          const row = (root?.[k + 'chatMessages'] || []).find((m) => m.id === assistantId)
          const tools = Array.isArray(row?.tools) ? row.tools : []
          const lastNoted = tools.length ? tools[tools.length - 1] : null
          const note = lastNoted && typeof lastNoted.note === 'string' ? lastNoted.note.trim() : ''
          if (note) {
            const said = String(finalText || '').trim()
            const endsOnCall = !_stripSuggestTail(buffered.slice(narratedUpto)).trim()
            if (repeatsNote(finalText, note)) {
              toolsPatch = tools.map((t, i) => (i === tools.length - 1 ? { ...t, note: null } : t))
            } else if (endsOnCall && (!said || said === _stripSuggestTail(buffered).trim())) {
              finalText = lastNoted.note
              toolsPatch = tools.map((t, i) => (i === tools.length - 1 ? { ...t, note: null } : t))
            }
          }
          // A turn the server refused for CREDITS (INSUFFICIENT_CREDITS /
          // SPEND_CAP_EXCEEDED / ALLOCATION_EXCEEDED) shows the shell's own
          // sentence and way out, never the server's prose — which named a
          // page the product does not have, told an admin to ask an admin, and
          // led with "Before this error, the following actions completed
          // successfully…". Last, so nothing above can put server text back.
          // The figures ride the metadata for the banner. Same gate as a
          // history reload: functions/creditsRefusal.js.
          const refused = _creditsRow(finalText, metadata, root)
          if (refused) {
            finalText = refused.text
            metadata = refused.metadata
          }
          _shellPatchAssistantMessage(
            root,
            assistantId,
            {
              ...(toolsPatch ? { tools: toolsPatch } : {}),
              text: finalText,
              streaming: false,
              suggestions,
              metadata,
              usage,
              sources,
              confidence,
              level
            },
            k
          )
          if (root?.update) {
            const patch = { [k + 'chatTyping']: false, [k + 'currentAssistantId']: null }
            // Track the conversation this turn used as the active thread — a NEW
            // thread's id is created server-side on the first message, so capture
            // it here (without it, the next message can't continue this thread).
            if (result?.conversationId) patch[k + 'activeSessionId'] = result.conversationId
            // Re-key the optimistic assistant message into the SERVER id-space so
            // the live buffer and the reloaded history (getConversation) share ONE
            // id-space. Without this the synthetic `a-…` id never matches the
            // server `_id`, so on the next thread switch/reload the message
            // duplicates or vanishes and id-keyed grouping/patching glitches (BUG 4).
            if (result?.messageId) {
              patch[k + 'chatMessages'] = (root[k + 'chatMessages'] || []).map((m) =>
                m && m.id === assistantId ? { ...m, id: String(result.messageId) } : m
              )
            }
            // Intent-specific UI hand-off — action proposals go to the
            // AiActionConfirmCard via _aiActionProposals (the SDK
            // populates that field when authMode==='ask'). build /
            // answer intents just render as the assistant text we
            // already appended above.
            if (
              result?.intent === 'action' &&
              Array.isArray(result.actions) &&
              result.requiresConfirmation
            ) {
              patch._aiActionProposals = result.actions
            } else if (result?.intent === 'build' && result.spec) {
              patch.freestyleBuildSpec = result.spec
            }
            root.update(patch)

            // BUILD-SPEC RENDER FALLBACK — if the cloud returned a build spec
            // with renderable DOMQL `source` but did NOT drive the
            // render_extension_page client tool itself (older agent / dispatch
            // path that only emits the spec), render it here so a prompt still
            // produces a live app. The render goes through the SAME guarded
            // injectPageInApp path (audit + scoped data bridge + isolated
            // iframe), so it's no less safe than the tool-driven path. Guard:
            // only when a source string is present AND no client-tool render
            // already happened this turn (the tool sets _addAppRendered:true).
            try {
              const spec = result?.intent === 'build' ? result.spec : null
              const specSource = spec && (spec.source || spec.domql || spec.code)
              if (
                specSource &&
                typeof specSource === 'string' &&
                !_extensionRenderedThisTurn &&
                scope === 'addapp'
              ) {
                const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
                if (callEl && typeof callEl.call === 'function') {
                  // Route through the guarded client-tool path (allow-list +
                  // audit + scoped bridge) rather than calling the render
                  // directly — same gate the cloud tool uses.
                  _runPlatformTool(callEl, 'injectPageInApp', [
                    {
                      name: spec.name || 'Extension',
                      source: specSource,
                      projectId: spec.projectId || spec.extensionId || null,
                      entities: spec.entities || spec.dataEntities || []
                    }
                  ]).catch((err) =>
                    console.warn('[simone] build-spec render fallback failed:', err?.message || err)
                  )
                }
              }
            } catch (err) {
              console.warn('[simone] build-spec render fallback threw:', err?.message || err)
            }
          }
          // Autosave the (scoped) thread list; add-app also refreshes its sidebar
          // since a build may have created/updated an extension.
          try {
            const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
            if (callEl && typeof callEl.call === 'function') {
              // The turn is over — settle the in-flight flag BEFORE the thread
              // list refresh, so a reload landing between the two never claims
              // an interruption that did not happen.
              callEl.call('markThreadTurn', scope, false)
              callEl.call('refreshSessionsAfterTurn', scope)
              if (scope === 'addapp') callEl.call('loadAddAppExtensions', root)
            }
          } catch (_) {
            /* best-effort — history just won't refresh until reload */
          }
          // Speak the reply (ElevenLabs, Simone's voice) — the SAME pipeline
          // wrote the text; speakReply only voices it. Fire-and-forget with
          // silent degrade (pref off / no key / autoplay refusal / live voice
          // session all no-op — the text is already on screen).
          try {
            const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
            if (finalText && callEl && typeof callEl.call === 'function') {
              callEl.call('speakReply', finalText)
            }
          } catch (_) {}
          _settleTurn()
        },
        onError: (err) => {
          if (turn.superseded) return
          _settleRetryingTools(root, assistantId, k)
          _cancelPendingFlush()
          // A STOPPED turn that failed on the server: nothing to say about it
          // (the person stopped it); the follow-up that waits for it may go.
          if (turn.stopped) {
            _stopElapsedTimer()
            try {
              const markEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
              if (markEl && typeof markEl.call === 'function') {
                markEl.call('markThreadTurn', scope, false)
              }
            } catch (_) {}
            if (root?.update && err && err.conversationId) {
              root.update({ [k + 'activeSessionId']: err.conversationId })
            }
            _settleTurn()
            return
          }
          if (!_goneThreadRetried && !buffered && err && err.status === 404) {
            _goneThreadRetried = true
            const freshId = `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
            turn.thread = freshId
            if (root?.update) root.update({ [k + 'activeSessionId']: freshId })
            ai.dispatch(
              { ..._turnPayload, conversationId: null, newConversation: true },
              _turnCallbacks
            )
            return
          }
          // A LOST CONNECTION — or a 409, a turn already running — while the
          // server works on: follow the turn, never declare it failed (see
          // _followDetachedTurn — sam, round 1b).
          const detachedConv = (err && err.conversationId) || activeConvId || null
          if (
            _turnMayStillRun(err) &&
            detachedConv &&
            !String(detachedConv).startsWith('s-') &&
            ai &&
            typeof ai.getConversation === 'function'
          ) {
            console.warn(
              '[simone] the connection dropped while the turn ran — following it:',
              err?.message || err
            )
            _followAfterDrop(err, detachedConv)
            return
          }
          _failTurn(err)
        },
        // Client (browser) tool: the cloud loop suspended and wants a platform
        // action run HERE, in the user's workspace UI, then the outcome posted
        // back so the loop resumes. The server already resolved the el.call fn +
        // positional args (data.fn / data.args); we run it through a guarded
        // allow-list and return the result for the SDK to submit. window.__wsRoot
        // is the live root element (app.js); _installerEl is the fallback.
        onToolCall: (callId, tool, args, data) => {
          // A superseded turn runs nothing and answers nothing: a result
          // handed back here would be POSTed into the new message's turn.
          if (turn.superseded) return new Promise(() => {})
          // Every call this page received: a follow after a dropped connection
          // never offers (and runs) one of them a second time, nor does a load.
          _noteClientCall(callId, root)
          // A stopped turn runs no client action: the refusal goes back to the
          // loop, which then ends sooner (and frees the conversation's lock).
          if (turn.stopped) {
            return Promise.resolve({
              ok: false,
              error: 'The person stopped this turn — this action did not run.'
            })
          }
          const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl
          const fn = (data && data.fn) || ''
          const fnArgs = Array.isArray(data?.args) ? data.args : Array.isArray(args) ? args : []
          // Thread THIS dispatch's root + scope through — chat-surface tools
          // (renderCards / askUser) must attach to the thread that asked, not
          // to whatever _cardScope guesses from global state (a suspended or
          // orphaned turn in another scope used to hijack the cards — the
          // "timeline shows ✓ but no card appears" bug).
          //
          // Through the authorization gate: in "Ask first" mode an effectful
          // call pauses on its timeline row (Run / Skip) until the user
          // answers; reads and Auto mode pass straight through.
          return runClientToolWithAuth(
            callEl,
            root,
            k,
            // `data.confirm` is the tool registry's own "ask the user" flag —
            // pass it through, or a confirm-gated tool that merely LOOKS
            // read-only runs with no consent (found live 2026-08-30).
            // `askText`: the person's own words for this turn — a create they
            // asked for runs at once (D2, runClientToolWithAuth).
            {
              callId,
              tool,
              fn,
              args: fnArgs,
              confirm: !!(data && data.confirm),
              readOnly: !!(data && data.readOnly),
              askText: text
            },
            () =>
              _rememberRun(
                root,
                turn.thread,
                { callId, tool, fn, readOnly: !!(data && data.readOnly) },
                _runPlatformTool(callEl, fn, fnArgs, {
                  root,
                  k,
                  readOnly: !!(data && data.readOnly),
                  callId
                })
              )
          ).then((res) => {
            // Superseded while it waited: the server closes the call under
            // the new message — the SDK must not post anything for it.
            if (res && res.superseded) return new Promise(() => {})
            // AI-301 (tickets/ai.md) — the truth about a write lives in the
            // tool's own return value, and only the CLIENT ever sees it (the
            // server's `completed` frame reduces it to a summary string).
            // Stamp the structured outcome onto the timeline row here, at the
            // only point it exists, so ChatToolRow can render the created item
            // as a live link. Pass-through: the SDK still gets `res` verbatim.
            const created = toolOutcome(res)
            if (created) {
              _patchToolActivity(root, { phase: 'result', callId, tool, created }, k)
            }
            // A REFUSED client tool names its reason on its row, and in the
            // console for whoever reads the run (Keepz DEV 2026-10-15: two
            // refused render_answer_ui draws showed a bare ✗ — a parse error
            // and a `Badge` refusal no one could see; the model's own retry
            // drew the third). Only the client holds the structured result —
            // the server's completed frame reduces it to the error code.
            // A step that could not run any more (expireParkedAiToolApproval)
            // already says so on its row, in the person's words; the reason
            // in `res` is the loop's.
            // A step the person SKIPPED is no failure (dev 2026-10-30: Skip
            // logged "[simone] delete_ticket failed: The user declined this
            // action."): the gate's own settle `{ ok: false, denied: true }`
            // (resolveAiToolApproval; a turn that ended while the step waited,
            // settleParkedAiToolApprovals) leaves the row "skipped" and the
            // console quiet — as a reload reads it (sessions.js
            // _skippedAtTheGate). The SDK still posts it as it is.
            const failure = res && (res.notRun || res.denied) ? null : toolFailure(res)
            if (failure) {
              _patchToolActivity(root, { phase: 'result', callId, tool, failure }, k)
              console.warn(`[simone] ${tool} failed: ${failure}`)
            }
            return res
          })
        },
        // Structured tool-activity frames for EVERY tool the loop runs (server
        // and client alike) — upserted onto the streaming message's `tools`
        // array, which ChatMessage renders as the live agentic timeline. On a
        // `started` frame, everything narrated since the last tool becomes
        // THIS row's reasoning note and the bubble resets for the next segment.
        onToolEvent: (evt) => {
          if (turn.stopped || turn.superseded) return
          // The server's close of a superseded step rides the NEW turn's
          // stream: that row already says what happened, where it sits.
          if (evt && evt.callId != null && _supersededCalls.has(String(evt.callId))) return
          // The server closed a question this turn still waits on (its close
          // of the call — another page's message, say): the card closes as
          // ended and nothing is posted; the frame adds nothing to the row.
          if (
            evt &&
            evt.phase === 'completed' &&
            evt.ok === false &&
            evt.callId != null &&
            _endWaitingCards(
              (w) => w.root === root && w.callId === String(evt.callId),
              'end',
              null
            ) > 0
          ) {
            return
          }
          if (evt && evt.phase === 'started') {
            // Cancel any queued delta flush BEFORE resetting the bubble — a
            // throttled flush firing after this reset would repaint the
            // previous segment's text into the freshly-emptied bubble.
            _cancelPendingFlush()
            const note = buffered.slice(narratedUpto).trim()
            narratedUpto = buffered.length
            if (note) evt = { ...evt, note }
            _shellPatchAssistantMessage(root, assistantId, { text: '' }, k)
          }
          _patchToolActivity(root, evt, k)
          // The page's write hooks: the add-app preview, the site builder and
          // the open canvas follow the turn's writes (_afterToolFrame).
          _afterToolFrame(evt)
        },
        // Discard boundary: the server threw away an unpersisted first draft
        // (leaked build call / scratchpad dump, see tickets/opus.md
        // AIUX-STREAM) and is about to rerun the whole loop from scratch. Both
        // generations stream over the SAME onChunk channel with no other
        // marker, so without this the retry's tokens silently continue the
        // first draft's bubble and the user watches one answer get replaced
        // by an unrelated one. Clear the buffer and blank the bubble at the
        // exact point the server discarded its draft, mirroring the
        // tool.activity 'started' bubble-reset above.
        onReset: () => {
          if (turn.stopped || turn.superseded) return
          _cancelPendingFlush()
          buffered = ''
          narratedUpto = 0
          _shellPatchAssistantMessage(root, assistantId, { text: '' }, k)
        },
        // Live active-process events from delegated/chained sub-agents. Maintain a
        // SCOPE-aware activeProcesses[] (k-prefixed, like the rest of the chat
        // state) that the Chat process panel renders: push on agent.started, patch
        // the matching entry on agent.completed. A fresh turn's first start RETAINS
        // the previous (all-finished) batch into runHistory (capped) — the
        // multi-agent view — instead of dropping it, then resets the live list.
        onProcessActive: (data) => {
          if (!root?.update || !data || turn.superseded) return
          let list = (root[k + 'activeProcesses'] || []).slice()
          // Undefined → leave runHistory untouched; set → retain the finished
          // batch. Applied together with activeProcesses in ONE root.update at the
          // end so the rotate is a single (not double) reactive invalidation.
          let nextHistory
          if (data.event === 'agent.started') {
            if (list.length && list.every((p) => p.status !== 'running')) {
              const hist = root[k + 'runHistory'] || []
              nextHistory = [...list.map((p) => ({ ...p })), ...hist].slice(0, 30)
              list = []
            }
            list.push({
              id: data.processId,
              agentId: data.agentId || '',
              agentName: data.agentName || '',
              seedSlug: data.seedSlug || null,
              role: data.role || '',
              mode: data.mode || '',
              step: typeof data.step === 'number' ? data.step : null,
              task: data.task || '',
              status: 'running',
              startedAt: Date.now()
            })
          } else if (data.event === 'agent.completed') {
            const idx = list.findIndex((p) => p.id === data.processId)
            const done = {
              status: data.status === 'error' ? 'error' : 'done',
              result: data.result || '',
              error: data.error || '',
              toolsUsed: Array.isArray(data.toolsUsed) ? data.toolsUsed : [],
              durationMs: data.durationMs || 0
            }
            if (idx >= 0) list[idx] = { ...list[idx], ...done }
            else {
              list.push({
                id: data.processId,
                agentId: data.agentId || '',
                agentName: data.agentName || '',
                seedSlug: data.seedSlug || null,
                ...done
              })
            }
          }
          root.update(
            nextHistory !== undefined
              ? { [k + 'activeProcesses']: list, [k + 'runHistory']: nextHistory }
              : { [k + 'activeProcesses']: list }
          )
        }
      })
    )
  }
}

// ── Local mode (targetMode 'local') — real simone-bridge round-trip ──────
// The drawer's Local target talks to the SAME local bridge that standalone
// simone / the chrome extension use. This transport keeps OWNING
// window.__simoneSendOverBridge and routes per send (local → WS port,
// anything else → sdk.ai), so the two transports never fight over the
// global. bridgeStatus stays honest per mode: in local mode it mirrors the
// real socket (composer disables while the bridge is down); leaving local
// restores sdk.ai's forced 'open'. Lifecycle is driven by the
// AssistantDrawer's targetMode watcher (app.js) via el.call.
const LOCAL_BRIDGE_URL = 'ws://localhost:8765'
let _localPort = null

export function syncSimoneLocalBridge() {
  if (typeof window === 'undefined') return
  // Prefer the calling element's live root (el.call binds `this`); fall back
  // to the installer's root for non-element callers.
  const root = (this && this.state && this.state.root) || _shellResolveRoot(null)
  if (!root || typeof root.update !== 'function') return
  const local = root.targetMode === 'local'

  if (local && !_localPort) {
    // Entering local mode — open the direct bridge socket. Status events are
    // applied only while local mode is STILL active (a late 'closed' after
    // switching back must not clobber sdk.ai's 'open').
    root.update({ bridgeStatus: 'connecting', bridgeUrl: LOCAL_BRIDGE_URL, agent: null })
    const port = createDirectWsPort(LOCAL_BRIDGE_URL)
    _localPort = port
    const sendToBridge = (payload) => {
      try {
        port.postMessage({ type: 'send', payload })
      } catch (_) {}
    }
    port.onMessage.addListener((msg) => {
      if (_localPort !== port) return // torn down — drop stragglers
      const live = _shellResolveRoot(null) || root
      if (!live || live.targetMode !== 'local') return
      if (msg.type === 'bridge_status') {
        live.update({ bridgeStatus: msg.status, bridgeUrl: LOCAL_BRIDGE_URL })
        return
      }
      if (msg.type === 'bridge_message') {
        handleBridgePayload(live, msg.payload, null, sendToBridge)
        return
      }
      handleAgentEvent(live, msg, sendToBridge)
    })
  } else if (!local && _localPort) {
    // Leaving local mode — tear the socket down (stops its reconnect loop)
    // and restore the sdk.ai transport's healthy status.
    try {
      _localPort.close()
    } catch (_) {}
    _localPort = null
    root.update({ bridgeStatus: 'open', bridgeUrl: 'sdk.ai', agent: 'sdk-ai', chatTyping: false })
  }
}

// Host hook fired by simone's TargetSwitcher on every mode click (see
// Chat.js → TargetSwitcher → Items onClick). The drawer can't watch
// targetMode reactively (smbls 3.14 dependency-tracking only re-renders
// elements that read the key), so the switcher tells us directly.
export function onTargetModeChange() {
  return syncSimoneLocalBridge.call(this)
}

// Hide the machine-readable follow-up trailer from the LIVE bubble while the
// answer streams — the server strips it from the FINAL text (and returns the
// parsed buttons as result.suggestions), but raw deltas arrive unstripped.
// Also hides a PARTIAL opening marker split across deltas ('<', '<!--SUG', …)
// so no fragment ever flashes; a legit answer ending in one of those prefixes
// gets it back when onDone swaps in the final text.
// All four machine markers (SUGGEST | SOURCES | CONFIDENCE | LEVEL), complete,
// cut off or split across deltas — the one stripper (_simone/answerTrailers.js).
const _stripSuggestTail = (text) => stripAnswerTrailers(text, { partial: true })

function _shellPatchAssistantMessage(root, id, patch, k = '') {
  if (!root?.update) return
  const list = root[k + 'chatMessages'] || []
  const next = list.map((m) => (m.id === id ? { ...m, ...patch } : m))
  root.update({ [k + 'chatMessages']: next })
}

function _shellPushSystemMessage(root, text, k = '') {
  if (!root?.update) return
  const next = (root[k + 'chatMessages'] || []).concat({
    id: `sys-${Date.now()}`,
    role: 'system',
    text,
    ts: Date.now()
  })
  root.update({ [k + 'chatMessages']: next })
}

// Extract the text parts of a server-serialized conversation message.
const _messageText = (m) => {
  if (!m) return ''
  if (typeof m.text === 'string' && m.text) return m.text
  const parts = Array.isArray(m.content) ? m.content : []
  return parts
    .filter((p) => p && p.type === 'text' && typeof p.text === 'string')
    .map((p) => p.text)
    .join('\n')
    .trim()
}

// After a turn error, the server may well have COMPLETED the turn — the
// client connection died, not the loop (proven live 2026-08-11: three
// "couldn't reach the server" bubbles whose real answers were persisted
// server-side, including an admission the user never saw). Re-read the
// conversation on a short backoff; if an assistant text message from AFTER
// the turn start exists, adopt it into the errored bubble. Every read hands
// the turn's settled steps (matched by the text it sent) to `onSteps`, so the
// page follows the writes the turn made all the same.
const _reconcileAfterTurnError = ({
  root,
  k,
  scope,
  assistantId,
  conversationId,
  ai,
  turnStartedAt,
  opts,
  sentText,
  onSteps
}) => {
  const convId = conversationId || (root && root[k + 'activeSessionId']) || null
  if (!convId || !ai || typeof ai.getConversation !== 'function') return
  const delays = [2500, 6000, 12000]
  let attempt = 0
  const tryOnce = () => {
    attempt++
    Promise.resolve()
      .then(() => ai.getConversation(convId, opts || {}))
      .then((conv) => {
        if (typeof onSteps === 'function') onSteps(_turnReadOf(conv, sentText).steps)
        const msgs =
          (conv && (conv.messages || conv.data?.messages || conv.conversation?.messages)) || []
        let adopted = null
        for (let i = msgs.length - 1; i >= 0; i--) {
          const m = msgs[i]
          if (!m || m.role !== 'assistant') continue
          const text = _messageText(m)
          if (!text) continue
          const ts = Date.parse(m.createdAt || m.created_at || '') || 0
          if (ts && ts >= turnStartedAt - 2000) {
            adopted = { text, id: m.id || m._id, metadata: m.metadata || null }
            break
          }
        }
        if (adopted) {
          // The turn the connection lost may have been the one the server
          // refused for credits: the shell's sentence, not its prose.
          const refused = _creditsRow(adopted.text, adopted.metadata, root)
          _shellPatchAssistantMessage(
            root,
            assistantId,
            {
              text: refused ? refused.text : adopted.text,
              ...(refused ? { metadata: refused.metadata } : {}),
              streaming: false,
              turnError: false,
              recovered: true
            },
            k
          )
          try {
            const callEl = (typeof window !== 'undefined' && window.__wsRoot) || null
            if (callEl && typeof callEl.call === 'function') {
              callEl.call('refreshSessionsAfterTurn', scope)
            }
          } catch (_) {}
          return
        }
        if (attempt < delays.length) setTimeout(tryOnce, delays[attempt])
      })
      .catch(() => {
        if (attempt < delays.length) setTimeout(tryOnce, delays[attempt])
      })
  }
  setTimeout(tryOnce, delays[0])
}

// ── A dropped connection is not a failed turn ───────────────────────────────
// sam, round 1b: the screen said "Sorry — I couldn't reach the server" twice
// (a CORS error in the console) while the server finished the turn — the
// answer showed only after the app was reopened — and an approval the turn
// waited on was settled as "skipped" by that same error. The SDK fails the
// whole turn when its event stream breaks (a proxy cuts a long stream; an
// error page without CORS headers reads as a bare fetch failure), but the
// server's loop keeps running. So a LOST CONNECTION detaches the turn instead
// of failing it: the bubble says the work goes on, a parked approval stays
// live, and the conversation is FOLLOWED until the answer lands — the result
// shows where it was asked. Only when the server never got the message, or
// nothing lands in the follow window, is the failure declared.

// The turn may still run: a lost connection — the same class
// userFacingErrorMessage words as "couldn't reach the server"
// (`NETWORK_UNREACHABLE`, the fetch-failure message shapes), a stream the SDK
// saw break (`STREAM_INTERRUPTED` / `STREAM_TRUNCATED`), a gateway's 502 / 503
// / 504 — or the server's own 409: a turn of this conversation is already
// running (CORE builder-r2: a slow builder turn answers 202 and streams). Any
// other status is the server's own answer: a failed turn.
const _turnMayStillRun = (err) => {
  if (!err) return false
  const status = Number(err.status)
  if (err.status != null && Number.isFinite(status) && status > 0) {
    return status === 409 || status === 502 || status === 503 || status === 504
  }
  const code = String(err.code || '')
  if (
    code === 'NETWORK_UNREACHABLE' ||
    code === 'STREAM_INTERRUPTED' ||
    code === 'STREAM_TRUNCATED'
  )
    return true
  return /failed to fetch|network unreachable|networkerror|load failed/i.test(
    String(err.message || '')
  )
}

// The SETTLED tool steps of a saved transcript, from message `from` on: a tool
// call and the result the server saved after it, in order —
// { callId, tool, args, ok }. `callId` is the call message's own id: the id the
// live frames named the call by (the server publishes the tool_call message's
// `_id` as the frame's callId). A call with no result yet is not settled.
const _settledStepsFrom = (msgs, from) => {
  const steps = []
  let open = null
  for (let i = Math.max(0, from); i < msgs.length; i++) {
    const m = msgs[i]
    const parts = (m && Array.isArray(m.content) && m.content) || []
    const call = parts.find((p) => p && p.type === 'tool_call')
    if (call) {
      const payload = call.payload || {}
      open = {
        callId: String(m.id || m._id || ''),
        tool: typeof payload.name === 'string' ? payload.name : '',
        args: payload.args && typeof payload.args === 'object' ? payload.args : null
      }
      continue
    }
    const result = parts.find((p) => p && p.type === 'tool_result')
    if (result && open) {
      if (open.callId && open.tool) {
        steps.push({ ...open, ok: !(result.payload && result.payload.ok === false) })
      }
      open = null
    }
  }
  return steps
}

// What a conversation read says about ONE turn, matched by the exact text the
// turn sent (the server saves the person's message as sent): no clock skew, and
// never an earlier turn's answer. → { arrived, answer, pending, closed(id), steps }:
//   arrived  the server holds this turn's message;
//   answer   its newest assistant TEXT after that message (a tool-call message
//            is a step, not an answer) — { text, id, metadata };
//   pending  the client call the server waits on, unless it is answered;
//   closed   a client call the server has a result for (answered or closed);
//   steps    the turn's settled tool steps (_settledStepsFrom).
const _turnReadOf = (conv, sentText) => {
  const data = conv && typeof conv === 'object' && conv.data && !conv.messages ? conv.data : conv
  const msgs = (data && (data.messages || data.conversation?.messages)) || []
  const sent = String(sentText || '').trim()
  let userIdx = -1
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]
    if (m && m.role === 'user' && _messageText(m).trim() === sent) {
      userIdx = i
      break
    }
  }
  const isStep = (m) =>
    (Array.isArray(m.content) && m.content.some((p) => p && p.type === 'tool_call')) ||
    (m.metadata && m.metadata.kind === 'tool_call')
  let answer = null
  for (let i = msgs.length - 1; userIdx >= 0 && i > userIdx; i--) {
    const m = msgs[i]
    if (!m || m.role !== 'assistant' || isStep(m)) continue
    const text = _messageText(m)
    if (text) {
      answer = { text, id: m.id || m._id || null, metadata: m.metadata || null }
      break
    }
  }
  // the transcript's own proof that a call is settled — one
  // `workspace_client_action` message per resolved call (the same reading as
  // _simone/pendingClientCall.js isClientCallResolved)
  const closed = (id) =>
    msgs.some(
      (m) =>
        m &&
        m.metadata &&
        m.metadata.kind === 'workspace_client_action' &&
        String(m.metadata.callId || '') === String(id)
    )
  const p = data && data.conversation && data.conversation.metadata
  const pending = normalizePendingClientCall(p && p.pendingClientCall)
  return {
    arrived: userIdx >= 0,
    answer,
    pending: pending && !closed(pending.callId) ? pending : null,
    closed,
    steps: userIdx >= 0 ? _settledStepsFrom(msgs, userIdx + 1) : []
  }
}

// Every client call this page received (a call the SDK may still be answering
// must never be offered — and run — a second time) and every one its person
// answered. Bounded: the newest 200 ids.
const _seenClientCalls = new Set()
const _noteClientCall = (id, root) => {
  if (!id) return
  _seenClientCalls.add(String(id))
  if (_seenClientCalls.size > 200) _seenClientCalls.delete(_seenClientCalls.values().next().value)
  if (root && typeof root === 'object') _boundedAdd(_callsOf(_seenOnRoot, root), String(id))
}

// ── E: a loaded conversation never re-runs a step by itself ──────────────────
// Dev 2026-10-27: opening a thread (a reload, a switch back) re-offered its
// waiting client call, and the gate ran a navigation or a write that had
// already run — the page jumped, a doc opened twice. Three guards, per page
// (the shell's root) and across a reload of the same tab:
//   1. a call this page already RAN is never run again by a load: the load
//      posts the result that run gave (the server resolves a call once, so a
//      second POST of it is only acknowledged); a call this page received
//      live and did not run (declined, stopped, waiting) is the live path's,
//      and a load does not offer it;
//   2. a WRITE that ran in this tab before a reload (its result never reached
//      the server) asks before it runs again — `smbls.ai.ranCalls` in
//      sessionStorage, per conversation; only another page load's record
//      counts (this page's own memory is guard 1);
//   3. a step that moves or covers the screen asks on load instead of running
//      (ON_LOAD_ASK_FNS). The chain after it is live: the person is there.
const _seenOnRoot = new WeakMap() // root → Set(callId) received live
const _ranOnRoot = new WeakMap() // root → Map(callId → that run's result promise)
const _callsOf = (map, root) => {
  let set = map.get(root)
  if (!set) map.set(root, (set = new Set()))
  return set
}
const _ranHere = (root, callId) => {
  const runs = root && typeof root === 'object' ? _ranOnRoot.get(root) : null
  return (runs && runs.get(String(callId))) || null
}
const _boundedAdd = (set, id) => {
  set.add(id)
  if (set.size > 200) set.delete(set.values().next().value)
}
const ON_LOAD_ASK_FNS = new Set([
  'openAppPage',
  'openPageInSplit',
  'selectPageItem',
  'renderAnswerUi',
  'injectPageInApp',
  'renderMultiPageApp',
  'aiStartTour',
  'changeGlobalTheme',
  'closeAllModals',
  'globalSearch'
])
const RAN_CALLS_KEY = 'smbls.ai.ranCalls'
// This page load's mark on its records — a record with another mark was
// written before a reload.
const _PAGE_LOAD = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
// Package-prefixed name: a served catalog page hoists every library's
// module-level helpers into ONE globalScope, and shared's bootTrail.js hoists
// its own `_session` (another body). The workspace-ui globalScope census fails
// on such a pair.
const _simoneSessionStore = () => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null
  } catch (_) {
    return null
  }
}
const _readRanCalls = () => {
  const store = _simoneSessionStore()
  if (!store) return {}
  try {
    const all = JSON.parse(store.getItem(RAN_CALLS_KEY) || '{}')
    return all && typeof all === 'object' && !Array.isArray(all) ? all : {}
  } catch (_) {
    return {}
  }
}
// A step RUNS (`running`, its result promise): remembered on this page with
// its result, and — a write in a real conversation — in this tab, so a reload
// before its result lands asks first. → `running`.
const _rememberRun = (root, conversationId, call, running) => {
  const id = String((call && call.callId) || '')
  if (!id) return running
  if (root && typeof root === 'object') {
    let runs = _ranOnRoot.get(root)
    if (!runs) _ranOnRoot.set(root, (runs = new Map()))
    runs.set(id, running)
    if (runs.size > 200) runs.delete(runs.keys().next().value)
  }
  const conv = String(conversationId || '')
  if (!conv || conv.startsWith('s-')) return running
  if (call.readOnly || isReadOnlyAiTool(call.tool, call.fn)) return running
  const store = _simoneSessionStore()
  if (!store) return running
  try {
    const all = _readRanCalls()
    const list = (Array.isArray(all[conv]) ? all[conv] : []).filter((e) => e && e.c !== id)
    list.push({ c: id, p: _PAGE_LOAD })
    delete all[conv]
    all[conv] = list.slice(-20)
    const convs = Object.keys(all)
    for (const old of convs.slice(0, Math.max(0, convs.length - 20))) delete all[old]
    store.setItem(RAN_CALLS_KEY, JSON.stringify(all))
  } catch (_) {
    /* a full or blocked store only loses the reload guard */
  }
  return running
}
const _ranBeforeReload = (conversationId, callId) => {
  const list = _readRanCalls()[String(conversationId || '')]
  return Array.isArray(list) && list.some((e) => e && e.c === String(callId) && e.p !== _PAGE_LOAD)
}

// ── B: a new message supersedes a step still waiting on Run / Skip ───────────
// The calls a new message superseded: their late frames (the server's own
// close of the call, on the NEW turn's stream) add no row anywhere.
const _supersededCalls = new Set()

// Follow a detached turn: one ordinary conversation read every `everyMs`, for
// at most `forMs` (the SDK's own follow window, TURN_BACKSTOP_MAX_MS = 11 min),
// while the turn is still this thread's (a newer send, a stop or a switch takes
// it). `ctx` callbacks:
//   onSteps(steps)      every read: the turn's settled tool steps so far
//                       (_turnReadOf `steps`) — the writes the lost stream
//                       never brought reach the page from here, before the
//                       answer of the same read;
//   onAnswer(answer)    the answer landed;
//   onHandOver(pending) the server waits on a client call this page never
//                       received (the stream was down when it asked): the
//                       reload path re-offers it (reofferPendingClientCall);
//   onWaiting()         the window ended while an approval still waits for
//                       the person — the row stays live, the person decides;
//   onGiveUp()          the server never got the message (`arrivalMs`), the
//                       conversation is gone, or nothing landed;
//   onLost()            a newer send, a stop or a thread switch took the thread
//                       — the follow ends quietly (the newest turn wins).
// A parked approval the server CLOSED meanwhile (answered elsewhere, or its
// own wait ended) is expired on its row — "did not run, ask again" — never
// left offering a Run the loop can no longer honour; a question card whose
// call it closed closes too (_endWaitingCards).
const _followDetachedTurn = (ctx) => {
  const { root, k, assistantId, conversationId, ai, sentText, opts } = ctx
  const t = {
    firstMs: 2500,
    everyMs: 4000,
    forMs: 11 * 60 * 1000,
    arrivalMs: 20000,
    ...(ctx.timing || {})
  }
  const started = Date.now()
  const ours = () => !!root && root[k + 'currentAssistantId'] === assistantId
  const mine = (held) => held && held.root === root && held.k === k
  const lost = () => {
    if (typeof ctx.onLost === 'function') ctx.onLost()
  }
  const tick = () => {
    if (!ours()) return lost()
    Promise.resolve()
      .then(() => ai.getConversation(conversationId, opts || {}))
      .then(
        (conv) => {
          if (!ours()) return lost()
          const read = _turnReadOf(conv, sentText)
          if (typeof ctx.onSteps === 'function') ctx.onSteps(read.steps)
          // A question whose call the transcript shows closed (the server's
          // own close, or an answer from another page) closes as ended.
          _endWaitingCards((w) => mine(w) && !!w.callId && read.closed(w.callId), 'end', null)
          if (read.answer) return ctx.onAnswer(read.answer)
          for (const id of Object.keys(_pendingToolApprovals)) {
            const waitsOnIt = read.pending && String(read.pending.callId) === id
            if (mine(_pendingToolApprovals[id]) && !waitsOnIt && read.closed(id)) {
              expireParkedAiToolApproval(id, 'workspace.simone.approval.expired')
            }
          }
          if (read.pending && !_seenClientCalls.has(String(read.pending.callId))) {
            return ctx.onHandOver(read.pending)
          }
          if (!read.arrived && Date.now() - started >= t.arrivalMs) return ctx.onGiveUp()
          if (Date.now() - started >= t.forMs) {
            const waiting = Object.keys(_pendingToolApprovals).some((id) =>
              mine(_pendingToolApprovals[id])
            )
            return waiting ? ctx.onWaiting() : ctx.onGiveUp()
          }
          setTimeout(tick, t.everyMs)
        },
        (err) => {
          if (!ours()) return lost()
          if (err && err.status === 404) return ctx.onGiveUp()
          if (Date.now() - started >= t.forMs) return ctx.onGiveUp()
          setTimeout(tick, t.everyMs)
        }
      )
  }
  setTimeout(tick, t.firstMs)
}

// ── Live agentic timeline ────────────────────────────────────────────────────
// Structured tool.activity frames ({ phase:'started'|'completed', callId, tool,
// args?, ok?, summary?, label? }) upsert a `tools` array on the streaming
// assistant message; ChatMessage renders it as the tool timeline (ChatToolRow
// rows with live status), so tool traffic never appears as message text.
//
// `label` (2026-09-30 per Nika "make ai responses more user friendly"; server
// lane ai-ux): the step in plain words, at most 80 characters, in the turn's
// language — ChatToolRow shows it instead of the tool name and its argument
// hint. The completed frame's words win (they may say what the step did); a
// frame without one keeps the row's. Every row carries the key — null when
// there is no label — so the row's factories track it from the first render (a
// key absent at first render is never tracked, and a label that arrives with
// the completed frame would never show).
// THE BUILD RETRIES ITSELF (persona round 3, B3: lina, priya, elena, aiko, giorgi, tomas —
// "The app could not be saved this time." two or three times in red before the app appeared,
// and a final-looking failure while the AI was still fixing it). A refused app write is the
// builder's own check: the model fixes it and calls again in the same turn. So while the turn
// runs, that row stays a running step (its failure held as `retryFailure`); a later write of
// the app that succeeds removes the refused rows; a turn that ends with no success shows them
// as failed, with the plain reason (_settleRetryingTools, onDone / onError).
const _BUILD_WRITE_TOOLS = new Set([
  'create_extension',
  'create_app',
  'update_app',
  'commit_project_changes'
])

function _settleRetryingTools(root, id, k = '') {
  const msg = (root?.[k + 'chatMessages'] || []).find((m) => m && m.id === id)
  const tools = Array.isArray(msg?.tools) ? msg.tools : []
  if (!tools.some((t) => t && t.retrying)) return
  const settled = tools.map((t) => {
    if (!t || !t.retrying) return t
    const { retrying, retryFailure, ...rest } = t
    return { ...rest, status: 'error', ...(retryFailure ? { failure: retryFailure } : {}) }
  })
  _shellPatchAssistantMessage(root, id, { tools: settled }, k)
}

const _toolLabel = (v) =>
  typeof v === 'string' && stripAnswerTrailers(v).trim() ? stripAnswerTrailers(v).trim() : null

function _patchToolActivity(root, evt, k = '') {
  if (!root?.update || !evt || !evt.tool) return
  let id = root[k + 'currentAssistantId']
  if (!id) {
    // A frame landing after onDone (late approval ask, straggler completion)
    // used to be dropped entirely — the parked approval then had NO visible
    // row or buttons while the server turn stayed suspended (the "user must
    // type continue" stall). Anchor it to the newest assistant message.
    const list = root[k + 'chatMessages'] || []
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i] && list[i].role === 'assistant') {
        id = list[i].id
        break
      }
    }
    if (!id) return
  }
  const msg = (root[k + 'chatMessages'] || []).find((m) => m.id === id)
  if (!msg) return
  const key = String(evt.callId || `${evt.tool}-${Date.now()}`)
  const tools = (msg.tools || []).slice()
  const idx = tools.findIndex((t) => t.id === key)
  if (evt.phase === 'started') {
    const entry = {
      id: key,
      tool: evt.tool,
      args: evt.args || (idx >= 0 ? tools[idx].args : null),
      // The model's narrated reasoning for this step (the text it streamed
      // before calling the tool) — rendered by ChatToolRow under the header.
      // Both this and `args` fall back to what the row ALREADY carries: a
      // started frame that names neither must not ERASE them. A history-
      // restored row holds the server's named-args object, and a re-offered
      // pending call re-starts that row (see reofferPendingClientCall) — the
      // approval card would otherwise ask about an action it cannot show.
      note: stripAnswerTrailers(evt.note) || (idx >= 0 ? tools[idx].note : null),
      label: _toolLabel(evt.label) || (idx >= 0 ? tools[idx].label || null : null),
      status: 'running',
      ts: Date.now()
    }
    if (idx >= 0) {
      // A started frame must never DEMOTE an authorization state — the server
      // emits started for client tools too, and its frame racing in after the
      // gate paused the row would flip "waiting for approval" back to a
      // spinner while nothing is actually running.
      const held = ['approval', 'denied', 'superseded'].includes(tools[idx].status)
      tools[idx] = { ...tools[idx], ...entry, ...(held ? { status: tools[idx].status } : {}) }
    } else tools.push(entry)
  } else if (evt.phase === 'approval') {
    // The authorization gate paused this call — the row renders Run / Skip and
    // resolveAiToolApproval settles it. Args from the started frame win (the
    // server's named-args object reads better than the positional array).
    const entry = {
      id: key,
      tool: evt.tool,
      args: (idx >= 0 && tools[idx].args) || evt.args || null,
      note: (idx >= 0 && tools[idx].note) || stripAnswerTrailers(evt.note) || null,
      label: (idx >= 0 && tools[idx].label) || _toolLabel(evt.label),
      status: 'approval',
      ts: idx >= 0 ? tools[idx].ts : Date.now()
    }
    if (idx >= 0) tools[idx] = { ...tools[idx], ...entry }
    else tools.push(entry)
  } else if (evt.phase === 'approved') {
    if (idx >= 0) tools[idx] = { ...tools[idx], status: 'running' }
  } else if (evt.phase === 'result') {
    // Structured outcome from the CLIENT tool's own return value (onToolCall
    // stamps it the moment the tool resolves — see toolOutcome.js). Merged in
    // place WITHOUT touching status: the server's `completed` frame arrives
    // after and spreads over the row, so `created` (a write's item) and
    // `failure` (a refusal's reason, toolFailure) survive into the final row.
    if (!evt.created && !evt.failure) return
    const stamp = {
      ...(evt.created ? { created: evt.created } : {}),
      ...(evt.failure ? { failure: evt.failure } : {})
    }
    if (idx >= 0) tools[idx] = { ...tools[idx], ...stamp }
    else {
      tools.push({
        id: key,
        tool: evt.tool,
        args: evt.args || null,
        label: _toolLabel(evt.label),
        ts: Date.now(),
        status: 'running',
        ...stamp
      })
    }
  } else if (evt.phase === 'denied') {
    const done = { status: 'denied', doneTs: Date.now() }
    if (idx >= 0) tools[idx] = { ...tools[idx], ...done }
    else {
      tools.push({
        id: key,
        tool: evt.tool,
        args: evt.args || null,
        label: _toolLabel(evt.label),
        ts: Date.now(),
        ...done
      })
    }
  } else {
    const summary = stripAnswerTrailers(evt.summary)
    // A SERVER tool's refusal has no client stamp: its reason is the summary's
    // first line ("I couldn't complete <tool>: <reason>"); a client stamp wins.
    const serverReason =
      evt.ok === false && !(idx >= 0 && tools[idx].failure)
        ? String(summary || '')
            .split('\n')[0]
            .trim()
        : ''
    const buildWrite = _BUILD_WRITE_TOOLS.has(evt.tool)
    // A failed step's summary line 2+ ("Details: <tool> failed — …") is the MODEL's, in
    // English (toolFailureLine.js on the server): the member's row keeps line 1 (B8, giorgi).
    const shown =
      evt.ok === false
        ? String(summary || '')
            .split('\n')[0]
            .trim()
        : summary
    const done = {
      status: evt.ok === false ? 'error' : 'done',
      summary: shown,
      ...(serverReason ? { failure: serverReason } : {}),
      // A step that never ran (expireParkedAiToolApproval): ChatToolRow shows no
      // words written before its call under it (F3) — they were never true.
      ...(evt.ok === false && evt.notRun ? { notRun: true } : {}),
      label: _toolLabel(evt.label) || (idx >= 0 ? tools[idx].label || null : null),
      doneTs: Date.now()
    }
    // A SERVER-run create names what it made on its completed frame (D2: a
    // task or an event the person asked for runs at once on the server, its
    // result carries the created id) — the same `created` a client tool
    // stamps, so the row reads Created · Open · Undo.
    const made = toolOutcome({ ok: evt.ok !== false, created: evt.created })
    if (made) done.created = made
    if (evt.ok === false && buildWrite) {
      done.status = 'running'
      done.retrying = true
      delete done.failure
      if (serverReason) done.retryFailure = serverReason
    }
    if (idx >= 0) {
      // A denied row stays denied — the loop's completed frame (carrying the
      // ok:false the denial itself produced) is bookkeeping, not a failure.
      // A superseded row stays superseded the same way.
      const kept = ['denied', 'superseded'].includes(tools[idx].status) ? tools[idx].status : ''
      tools[idx] = { ...tools[idx], ...done, ...(kept ? { status: kept } : {}) }
    } else tools.push({ id: key, tool: evt.tool, args: evt.args || null, ts: Date.now(), ...done })
    // The app was written: the refused tries before it are not failures any more.
    if (evt.ok !== false && buildWrite) {
      for (let i = tools.length - 1; i >= 0; i--) {
        if (tools[i] && tools[i].retrying && _BUILD_WRITE_TOOLS.has(tools[i].tool))
          tools.splice(i, 1)
      }
    }
    // An EMBED tool's live block (embed_records / embed_widget): the frame's
    // `embed` becomes the thread card (_simone/toolEmbed.js) on this message,
    // ONCE per call — the server may re-publish a completed frame. A frame
    // without `embed` adds nothing: inert until the server sends it.
    const embedCard = toolEmbedCard(evt)
    const cards = Array.isArray(msg.cards) ? msg.cards : []
    if (embedCard && !cards.some((c) => c && c._embedFor === key)) {
      _shellPatchAssistantMessage(
        root,
        id,
        { tools, cards: cards.concat({ ...embedCard, _embedFor: key }) },
        k
      )
      return
    }
  }
  _shellPatchAssistantMessage(root, id, { tools }, k)
}

// The items the member UNDID in this scope's thread: ChatToolRow's Undo deletes
// a created item and marks its id in `root._aiUndone`. The thread's history
// still holds the create step, so without this the next reply said the item
// exists (wave-2 (1), dev 2026-10-28: "I've added one event … oak qa sync"
// after its Undo). → [{ kind, title }], newest last, at most 5.
const _undoneInThread = (root, k = '') => {
  const undone = root && root._aiUndone
  if (!undone || typeof undone !== 'object') return []
  const out = []
  for (const m of root[k + 'chatMessages'] || []) {
    for (const t of m && Array.isArray(m.tools) ? m.tools : []) {
      const c = t && t.created
      if (c && c.id != null && undone[String(c.id)]) {
        out.push({ kind: String(c.kind || 'item'), title: String(c.title || '').slice(0, 120) })
      }
    }
  }
  return out.slice(-5)
}

// ── Interactive assistant cards ──────────────────────────────────────────────
// The assistant renders rich UI (ticket/people/thumbnail cards, choice/confirm
// buttons) by attaching a `cards` array to its message; ChatMessage renders them
// via the ChatCard dispatcher. ask_user suspends the turn on a card and resumes
// when the user clicks a button (resolveChatCard → the suspended Promise).

// cardId → { root, k, thread, callId, answer(value, label), supersede(),
// end(reason, said) } — the card's thread, its step's call, and its ways out:
// the person's pick, a new message (supersedeParkedAiToolApprovals), or the
// end of its turn without the answer (_endWaitingCards).
const _pendingCardResolves = {}
let _cardSeq = 0

// The waits (parked approvals, question cards) of one chat scope's thread
// `conversationId`. A wait parked before its thread had a server id (a local
// 's-…' thread) is this thread's too.
const _waitsInThread = (wait, root, k, conversationId) => {
  if (!wait || wait.root !== root || String(wait.k || '') !== String(k || '')) return false
  const thread = String(wait.thread || '')
  const conv = conversationId == null ? '' : String(conversationId)
  return !(thread && conv && thread !== conv && !thread.startsWith('s-'))
}

// THE TURN ENDED WITHOUT THE ANSWER (F1, 2026-10-28 — the VOICE lane's open
// risk): a stopped or failed turn left its question card waiting — live
// buttons in the drawer and on the call screen (AiAsk reads an unresolved card
// as a live ask) until an answer or a new message, and a press resolved a
// promise no turn waited for. A stop during a question also held the next
// message back for the whole stopped-turn wait: the server's loop waited on
// the question, so the stopped turn never settled. Each waiting card `match`
// takes closes and says why (`reason`: 'stop' | 'end'); `said` (the
// sentence the model reads, or nothing to post) as for `end` in _askOnCard.
// → how many it closed.
const _endWaitingCards = (match, reason, said) => {
  let count = 0
  for (const cardId of Object.keys(_pendingCardResolves)) {
    const wait = _pendingCardResolves[cardId]
    if (!wait || !match(wait)) continue
    delete _pendingCardResolves[cardId]
    count += 1
    try {
      wait.end(reason, said)
    } catch (_) {}
  }
  return count
}

// Which scope's buffer is the active turn writing to (general '' vs add-app
// '_ax_' vs dashboard '_db_')? Prefer general; use a scoped surface only when it
// alone has a live turn — so render_cards / ask_user attach their interactive
// cards to the RIGHT thread (e.g. the dashboard inline thread, not the drawer).
function _cardScope(root) {
  if (!root || root.currentAssistantId) return ''
  if (root._db_currentAssistantId) return '_db_'
  if (root._ax_currentAssistantId) return '_ax_'
  return ''
}

// Append `cards` to the message currently streaming in this scope, or push a
// fresh assistant message carrying them. Returns the message id.
function _attachCards(root, cards, k = '') {
  if (!root?.update) return null
  const id = root[k + 'currentAssistantId']
  const list = root[k + 'chatMessages'] || []
  const existing = id ? list.find((m) => m.id === id) : null
  if (existing) {
    _shellPatchAssistantMessage(root, id, { cards: (existing.cards || []).concat(cards) }, k)
    return id
  }
  const newId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
  root.update({
    [k + 'chatMessages']: list.concat({
      id: newId,
      role: 'assistant',
      text: '',
      cards,
      ts: Date.now()
    })
  })
  return newId
}

// APPS mode (AI_ASSISTANT_SPEC §12.1: "a small app surface INSIDE this
// workspace, previewed inline in the thread, then 'Save as app' installs it"):
// while the MAIN THREAD holds the main area, a built app is previewed IN the
// thread — registered as a view (functions/extensionPreview.js, open: false)
// and carried by the turn as a `view` card, whose block offers Save as app
// (functions/assistantThreadBlocks.js). Anywhere else the split sheet opens,
// as before. `_previewInThread` answers whether the thread holds the main
// area; `_attachPreviewCard` hangs the preview on the turn as a `view` card.
function _previewInThread(root) {
  return !!(root && root._homeThreadOpen === true && root.assistantDock === 'main')
}
function _attachPreviewCard(root, scope, pv, name) {
  if (!pv || !pv.ok || !pv.opened) return
  const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
  try {
    _attachCards(root, [{ type: 'view', route: pv.opened, title: pv.title || name || '' }], k)
  } catch (_) {
    /* no thread to attach to — the preview stays registered */
  }
}

// Mark a choice/confirm card answered: ChatCard folds it to its answer line
// (`_resolved`, `_chosen`, `_chosenLabel`). `extra` says how the person
// answered when it was not a button: `written` (their own words) or `edit` (a
// confirm card's Edit, the words say what should change).
function _markCardResolved(root, msgId, cardId, value, label, k = '', extra) {
  if (!root?.update) return
  const list = root[k + 'chatMessages'] || []
  const next = list.map((m) => {
    if (m.id !== msgId) return m
    const cards = (m.cards || []).map((c) =>
      c._cardId === cardId
        ? {
            ...c,
            _resolved: true,
            _chosen: value,
            _chosenLabel: label,
            _written: !!(extra && extra.written),
            _edit: !!(extra && extra.edit)
          }
        : c
    )
    return { ...m, cards }
  })
  root.update({ [k + 'chatMessages']: next })
}

// Every key a question card's factories read, present from its first render
// (a childrenAs:'state' row tracks only the keys it was built with).
const _ASK_CARD_SEED = {
  _resolved: false,
  _chosen: null,
  _chosenLabel: null,
  _superseded: false,
  _closedBy: null,
  _written: false,
  _edit: false
}

// The person's own words from a card's field (Write my own / Edit).
const _cardWords = (extra) =>
  extra && typeof extra.text === 'string' ? extra.text.trim().slice(0, 2000) : ''

// What the step row names as the answer ("You chose “Red”"). An Edit is no
// choice: the row says "You answered" (no answer) and the card under it shows
// the words the person asked for.
const _rowAnswer = (shown, extra) => (extra && extra.edit ? '' : String(shown || ''))

// What the model reads back from an ask_user card. A button gives its value
// and label; on a confirm card also `confirmed`. Words the person wrote
// instead of picking come back as the answer itself, with `written: true`. A
// confirm card's Edit comes back NOT confirmed, the words as `edit`: never a
// yes (the server's answeredYes reads `confirmed` first).
const _askAnswer = (kind, value, label, extra) => {
  const words = _cardWords(extra)
  if (kind === 'confirm') {
    if (extra && extra.edit) {
      return { ok: true, choice: 'edit', label: words, confirmed: false, edit: words }
    }
    return { ok: true, choice: value, label: label || value, confirmed: value === 'confirm' }
  }
  if (extra && extra.written) return { ok: true, choice: words, label: words, written: true }
  return { ok: true, choice: value, label: label || value }
}

// What the model reads back from confirm_with_user: `confirmed`, as the
// modal answered, and the person's `edit` when they asked for a change.
const _confirmAnswer = (value, extra) =>
  extra && extra.edit
    ? { ok: true, confirmed: false, edit: _cardWords(extra) }
    : { ok: true, confirmed: value === 'confirm' }

// A question card goes on the message that holds its step, so it is drawn
// under that step: a question asked again after a reload (the turn's id is
// clear then) went onto a new empty message, away from its step.
function _attachAskCard(root, card, callId, k = '') {
  const list = (root && root[k + 'chatMessages']) || []
  const host = callId
    ? list.find(
        (m) =>
          m &&
          m.role === 'assistant' &&
          Array.isArray(m.tools) &&
          m.tools.some((t) => t && String(t.id) === callId)
      )
    : null
  if (!host) return _attachCards(root, [card], k)
  _shellPatchAssistantMessage(root, host.id, { cards: (host.cards || []).concat(card) }, k)
  return host.id
}

// ONE QUESTION, ONE CARD, IN THE THREAD THAT ASKED: attach the card, stop the
// typing dots, and wait for the person: a button, their own words, or a new
// message that closes it (supersede). The card carries its step's call id
// (`_callId`), so ChatMessage draws it under the step that asked.
// `result(value, label, extra)` is what the model reads back.
async function _askOnCard(root, k, card, scope, result) {
  const cardId = `card-${++_cardSeq}-${Date.now()}`
  const callId = scope && scope.callId != null ? String(scope.callId) : ''
  const msgId = _attachAskCard(
    root,
    { ...card, ..._ASK_CARD_SEED, _cardId: cardId, _callId: callId || null },
    callId,
    k
  )
  // The AI is now waiting on the user — stop the typing indicator and the
  // streaming dots on this message.
  try {
    root.update({ [k + 'chatTyping']: false })
    if (msgId) _shellPatchAssistantMessage(root, msgId, { streaming: false }, k)
  } catch (_) {}
  return await new Promise((resolve) => {
    _pendingCardResolves[cardId] = {
      root,
      k,
      // The conversation it waits in — a new message there closes it.
      thread: (root && root[k + 'activeSessionId']) || null,
      // Its step's call — the server's close of that call closes it.
      callId,
      answer: (value, label, extra) => {
        const words = _cardWords(extra)
        const shown = (extra && (extra.written || extra.edit) && words) || label || value
        try {
          _markCardResolved(root, msgId, cardId, value, shown, k, extra)
        } catch (_) {}
        // The row names the pick (W7 live, 2026-10-28: an answered
        // question still read "Waiting for your choice"); the server's
        // completed frame spreads over the row and keeps it.
        if (callId) {
          try {
            _patchToolRow(root, k, callId, { answer: _rowAnswer(shown, extra) })
          } catch (_) {}
        }
        // The answer resumes the SAME turn — it works on (a first build
        // runs for minutes). Show it working again while this scope still
        // has a turn running; without this the builder looked idle after
        // "Yes, build the board", and the person typed into a turn that was
        // still building (aiko-r2, marcus-r2). A card answered after its
        // turn ended changes nothing.
        try {
          if (root && root[k + 'currentAssistantId']) {
            root.update({ [k + 'chatTyping']: true })
          }
        } catch (_) {}
        resolve(result(value, label, extra))
      },
      // The person wrote instead of picking (dev 2026-10-28, kestrel-
      // assist "Add a lead" → "Never mind…"): the server closes the call
      // under the new message (closeDanglingClientCall, reason
      // `new_message`), so nothing is posted for it, its row reads
      // superseded, and the server's close frame adds no second row.
      supersede: () => {
        if (callId) _boundedAdd(_supersededCalls, callId)
        try {
          _markCardSuperseded(root, msgId, cardId, k)
        } catch (_) {}
        if (callId) _patchToolRow(root, k, callId, { status: 'superseded', doneTs: Date.now() })
        resolve({ ok: false, superseded: true, notRun: true, reason: 'new_message' })
      },
      // Its turn ended without the answer (_endWaitingCards): the card closes
      // and says why (`_closedBy` — 'stop': the person stopped the reply;
      // 'end': the turn failed, or the server closed the call), and its step
      // stops waiting: a quiet "Unanswered question", never "Waiting for your
      // choice" (the server's label for the wait). `said`: the loop still
      // waits on the call (a stop, a failure) — the SDK posts this not-run
      // result with that sentence for the model, so the loop ends and frees
      // the thread for the next message. A call the server closed itself
      // gets nothing (no `said`): `superseded`, which the transport never
      // hands to the SDK. Its late frames add nothing.
      end: (reason, said) => {
        if (callId) _boundedAdd(_supersededCalls, callId)
        try {
          _markCardSuperseded(root, msgId, cardId, k, reason)
        } catch (_) {}
        if (callId) {
          _patchToolRow(root, k, callId, {
            status: 'denied',
            label: _sayKey('ai.toolStep.askUnanswered'),
            doneTs: Date.now()
          })
        }
        if (!said) {
          resolve({ ok: false, superseded: true, notRun: true, reason: 'closed' })
          return
        }
        resolve({
          ok: false,
          notRun: true,
          ...(reason === 'stop' ? { stopped: true } : {}),
          error: said
        })
      }
    }
  })
}

// A card a new message closed: no pick, its buttons inert, and it says why
// (ChatCard reads `_superseded`). `closedBy`: its turn ended without the
// answer instead ('stop' | 'end' — the card says which).
function _markCardSuperseded(root, msgId, cardId, k = '', closedBy = null) {
  if (!root?.update) return
  const list = root[k + 'chatMessages'] || []
  const next = list.map((m) => {
    if (m.id !== msgId) return m
    const cards = (m.cards || []).map((c) =>
      c._cardId === cardId ? { ...c, _resolved: true, _superseded: true, _closedBy: closedBy } : c
    )
    return { ...m, cards }
  })
  root.update({ [k + 'chatMessages']: next })
}

// el.call('resolveChatCard', cardId, value, label, extra) — fired by a
// choice/confirm button, or by the card's own field (`extra`: `{ written,
// text }` for Write my own, `{ edit, text }` for a confirm card's Edit), to
// resume the suspended tool with the person's answer. True when a waiting
// question took it.
export function resolveChatCard(cardId, value, label, extra) {
  const wait = _pendingCardResolves[cardId]
  if (!wait) return false
  delete _pendingCardResolves[cardId]
  try {
    wait.answer(value, label, extra)
  } catch (_) {}
  return true
}

// el.call('changeChatCardAnswer', cardId, value, label, extra) — CHANGE AN
// ANSWER (Nika 2026-10-28: "make it able to also edit"). The answered card's
// Change control: the turn that asked has its answer already, so the new pick
// (or the person's own words, `extra` as for resolveChatCard) goes to the AI
// as the person's next message, in their language, and the card and its step
// show the new answer. A card a new message closed has no answer to change.
// Found by its id in every scope's thread; `reason` is a code for the caller.
export function changeChatCardAnswer(cardId, value, label, extra) {
  const el = this && typeof this.call === 'function' ? this : _creditsEl()
  const root = (el && el.state && (el.state.root || el.state)) || _shellResolveRoot(null)
  const id = String(cardId || '')
  if (!el || typeof el.call !== 'function' || !root || !root.update || !id) {
    return { ok: false, reason: 'no-ui' }
  }
  for (const [k, scope] of _ROW_SCOPES) {
    const msg = (root[k + 'chatMessages'] || []).find(
      (m) => m && Array.isArray(m.cards) && m.cards.some((c) => c && c._cardId === id)
    )
    if (!msg) continue
    const card = msg.cards.find((c) => c && c._cardId === id)
    if (!card._resolved || card._superseded) return { ok: false, reason: 'not-answered' }
    const edited = !!(extra && (extra.written || extra.edit))
    const answer = String((edited ? _cardWords(extra) : label || value) || '').trim()
    if (!answer) return { ok: false, reason: 'empty' }
    _markCardResolved(
      root,
      msg.id,
      id,
      edited ? (extra.edit ? 'edit' : answer) : value,
      answer,
      k,
      extra
    )
    if (card._callId) _patchToolRow(root, k, card._callId, { answer: _rowAnswer(answer, extra) })
    const prompt = String(card.prompt || '').trim()
    const question = prompt.length > 120 ? prompt.slice(0, 119).trimEnd() + '…' : prompt
    const words = String(
      el.call(
        'polyglot',
        question ? 'ai.chatCard.ask.changeMessage' : 'ai.chatCard.ask.changeMessageShort'
      ) || ''
    )
      .replace('{question}', () => question)
      .replace('{answer}', () => answer)
    el.call('sendPrompt', words, scope)
    return { ok: true, sent: true }
  }
  return { ok: false, reason: 'not-found' }
}

// ── Client-tool authorization (the "Ask first" contract) ────────────────────
// The AI settings panel has always offered `Ask first — confirm actions before
// they run` vs `Auto — run actions immediately` (sdk.ai.get/setAuthMode, with
// an admin workspace pin), and the server receives that mode with every turn —
// but the CLIENT tool path never enforced it: onToolCall ran every platform
// action the moment the loop asked. This section is the enforcement.
//
// Mechanism mirrors ask_user's suspended-Promise spine exactly
// (_pendingCardResolves/resolveChatCard above): the gate pauses the tool call
// on a Promise, surfaces an `approval` frame on the SAME live timeline row the
// tool already occupies (ChatToolRow renders Run / Skip on it), and
// resolveAiToolApproval — fired by those buttons via el.call — settles it.
// Deny resolves { ok:false, denied:true } so the loop continues honestly
// instead of hanging or fabricating success.
//
// READ-ONLY calls never prompt, in either mode. Asking permission to LOOK
// (list_projects, get_project, excalidrawGetScene) would bury the one ask that
// matters — consent for writes — under a pile of asks nobody reads. The
// classifier below is deliberately conservative: an unknown, unclassifiable
// tool is treated as a WRITE and gated.

const _pendingToolApprovals = {} // callId → { root, k, run, resolve }

// Chat-surface + navigation + presentation tools: these ARE the conversation
// (ask_user is itself the consent mechanism) or change nothing but what the
// user is looking at. Both server (snake) and el.call (camel) names — frames
// carry one, dispatch data the other.
const READ_ONLY_AI_TOOLS = new Set([
  'open_search',
  'navigate_to_app',
  'open_page_in_split',
  'select_page_item',
  'render_answer_ui',
  'render_cards',
  'render_extension_page',
  'ask_user',
  'confirm_with_user',
  'close_modals',
  'set_theme',
  'openSearch',
  'openAppPage',
  'openPageInSplit',
  'selectPageItem',
  'renderAnswerUi',
  'renderCards',
  'askUser',
  'confirmWithUser',
  'closeAllModals',
  'changeGlobalTheme',
  'injectPageInApp',
  // create_app/update_app have already saved the draft on the server. This
  // follow-on call only shows it in the preview, like injectPageInApp above.
  'renderMultiPageApp',
  // Proposing an app action only parks it and shows the Approve / Reject
  // card — the card IS the consent, and nothing runs before its click. A
  // Run / Skip row in front of it asked twice (MODULE-ACTIONS-PROPOSAL-NEVER-
  // OFFERED-1). Approve / reject are never AI-callable at all.
  'propose_app_action',
  'proposeModuleAction',
  // Showing what the member can do here changes nothing — the card's rows
  // only send an ask when tapped.
  'describe_capabilities',
  'aiDescribeCapabilities',
  // The quick tour only shows the member their own screen, stop by stop
  // (functions/aiStartTour.js); Escape ends it.
  'start_tour',
  'aiStartTour'
])

export function isReadOnlyAiTool(tool, fn) {
  const t = String(tool || '')
  if (READ_ONLY_AI_TOOLS.has(t)) return true
  if (/^(list|get|read|search|find|fetch|check)_/.test(t)) return true
  const f = String(fn || '')
  if (READ_ONLY_AI_TOOLS.has(f)) return true
  if (/^(list|get|read|search|find|fetch|check)(?=[A-Z]|$)/.test(f)) return true
  // Module fns carry their app as a lowercase prefix (excalidrawGetScene) —
  // strip it and classify by the verb that follows.
  const m = f.match(/^[a-z0-9]+/)
  const rest = m ? f.slice(m[0].length) : ''
  return /^(Get|List|Read|Search|Find|Fetch|Check)(?=[A-Z]|$)/.test(rest)
}

// ── D9: an AI delete asks in EVERY mode ──────────────────────────────────────
// CRUD standard D9 (orchestrator `aihouse`, 2026-10-30): "AI deletes ask in
// every mode, Auto included". Auto mode and a turn's "Run all" grant let the
// other writes run at once; a delete (aiDeleteFile / aiDeleteNote /
// aiDeleteTicket / aiDeleteWidget, and the server tools that name them) parks a
// Run / Skip row every time. The server's delete tools carry no `confirm` flag
// (workspaceTools.js delete_file / delete_note / delete_ticket / delete_widget),
// so the client names them by the verb — a new aiDelete* asks too.
export function isAiDeleteTool(tool, fn) {
  return /^aiDelete[A-Z]/.test(String(fn || '')) || /^delete_/.test(String(tool || ''))
}

// ── D2: a create the person asked for runs at once ───────────────────────────
// Nika 2026-10-28 (workspace AI overhaul, decision D2): "asked creates run at
// once with Undo; edits, deletes, sends and AI-suggested writes still ask".
// Only the creates a timeline row can UNDO (ChatToolRow UndoCreated →
// aiUndoCreated): a note and a ticket. A folder and a channel have no Undo,
// and a widget's preview IS its consent, so they keep the ask.
const ASKED_CREATE_FNS = {
  aiCreateNote: 'note',
  create_note: 'note',
  aiCreateTicket: 'ticket',
  create_ticket: 'ticket'
}
// A runaway loop is not the person's ask: past this many asked creates in one
// turn, the next one asks again (Run all still grants the rest of the turn).
const ASKED_CREATES_PER_TURN = 10
const _askedCreatesRun = new Map() // `${k}|${turnId}` → count

// English: a create verb, then its object inside the phrase the verb governs
// (a preposition ends the phrase — "a document WITH a strategy" is one doc).
const _EN_CREATE_VERB =
  /\b(set up|jot down|note down|write up|write down|put together|create|make|add|write|draft|schedule|book|prepare|compose|generate|produce|log|file)\b/g
const _EN_CREATE_OBJECT = {
  note: 'note',
  doc: 'note',
  document: 'note',
  memo: 'note',
  plan: 'note',
  outline: 'note',
  brief: 'note',
  report: 'note',
  proposal: 'note',
  strategy: 'note',
  summary: 'note',
  'write-up': 'note',
  writeup: 'note',
  article: 'note',
  agenda: 'note',
  checklist: 'note',
  spec: 'note',
  minutes: 'note',
  task: 'ticket',
  todo: 'ticket',
  'to-do': 'ticket',
  ticket: 'ticket',
  reminder: 'ticket',
  issue: 'ticket',
  bug: 'ticket',
  event: 'event',
  meeting: 'event',
  appointment: 'event',
  folder: 'folder'
}
const _EN_PHRASE_END = new Set([
  'with',
  'about',
  'for',
  'from',
  'of',
  'on',
  'in',
  'into',
  'to',
  'regarding',
  'that',
  'which',
  'where',
  'so',
  'because',
  'using',
  'based',
  'by',
  'at',
  'called',
  'named',
  'titled'
])
const _enObjectKind = (word) => {
  if (_EN_CREATE_OBJECT[word]) return _EN_CREATE_OBJECT[word]
  if (word.endsWith('ies')) return _EN_CREATE_OBJECT[word.slice(0, -3) + 'y'] || null
  if (word.endsWith('s')) return _EN_CREATE_OBJECT[word.slice(0, -1)] || null
  return null
}
const _enAskedKinds = (sentence, out) => {
  const s = sentence.toLowerCase().replace(/[’`]/g, "'").trim()
  if (!s) return
  // A question about a thing, or about how to make one, asks for nothing.
  if (
    /^(how|what|why|where|when|which|who|whom|whose|is|are|was|were|do|does|did|have|has|had|should|shall)\b/.test(
      s
    ) ||
    /^(can|could|may|might)\s+(i|we)\b/.test(s)
  )
    return
  if (/\b(don't|dont|do not|never|no need to)\b/.test(s)) return
  const objectsAfter = (rest) => {
    const words = rest
      .split(/[^a-z0-9'-]+/)
      .filter(Boolean)
      .slice(0, 8)
    for (const w of words) {
      if (_EN_PHRASE_END.has(w)) break
      const kind = _enObjectKind(w)
      if (kind) out.add(kind)
    }
    return words
  }
  // "New task: renew the domain" — a quick capture.
  if (/^new\s/.test(s)) objectsAfter(s.slice(4))
  _EN_CREATE_VERB.lastIndex = 0
  let m
  while ((m = _EN_CREATE_VERB.exec(s))) {
    const rest = s.slice(m.index + m[0].length)
    const first = rest.split(/[^a-z0-9'-]+/).filter(Boolean)[0] || ''
    // "add the notes to the doc" edits a thing; "make sure…" makes nothing.
    if (m[1] === 'add' && /^(the|this|that|these|those|it|my|our|your|his|her|their)$/.test(first))
      continue
    if (m[1] === 'make' && /^(sure|certain|it|that|me|us|them|him|her)$/.test(first)) continue
    objectsAfter(rest)
  }
}

// Georgian: word order is free, so a create verb stem and an object stem in
// the same sentence. A question word or a negation in it asks for nothing.
const _KA_CREATE_VERB = [
  'შექმენ',
  'შექმნ',
  'შევქმნათ',
  'დაამატ',
  'დავამატოთ',
  'დაწერ',
  'დამიწერ',
  'ჩაინიშნ',
  'ჩამიწერ',
  'დაგეგმ',
  'გააკეთ',
  'შეადგინ',
  'მოამზად',
  'დანიშნ'
]
// "შექმნილი" — "the created …" — names a thing that exists.
const _KA_NOT_VERB = ['შექმნილ']
const _KA_CREATE_OBJECT = [
  ['შენიშვნ', 'note'],
  ['ჩანაწერ', 'note'],
  ['დოკუმენტ', 'note'],
  ['გეგმ', 'note'],
  ['სტრატეგი', 'note'],
  ['მოხსენებ', 'note'],
  ['რეზიუმე', 'note'],
  ['დავალებ', 'ticket'],
  ['ამოცან', 'ticket'],
  ['შეხსენებ', 'ticket'],
  ['შეხვედრ', 'event'],
  ['ღონისძიებ', 'event'],
  ['საქაღალდ', 'folder']
]
const _KA_QUESTION = new Set(['როგორ', 'რატომ', 'სად', 'როდის', 'ვინ', 'რა', 'რომელი'])
const _KA_NEGATION = new Set(['ნუ', 'არ'])
const _kaAskedKinds = (sentence, out) => {
  const words = sentence.split(/[\s,.;:!?"'«»„“”()[\]{}—–]+/).filter(Boolean)
  let verb = false
  const kinds = []
  for (const w of words) {
    if (_KA_QUESTION.has(w) || _KA_NEGATION.has(w)) return
    if (_KA_NOT_VERB.some((p) => w.startsWith(p))) continue
    if (_KA_CREATE_VERB.some((p) => w.startsWith(p))) verb = true
    const obj = _KA_CREATE_OBJECT.find(([p]) => w.startsWith(p))
    if (obj) kinds.push(obj[1])
  }
  if (verb) for (const kind of kinds) out.add(kind)
}

// el.call('aiAskedCreateKinds', text) — the kinds of thing the person's OWN
// words ask to be made: 'note' | 'ticket' | 'event' | 'folder'. Conservative on
// purpose: a question ("how do I add a task?", "what is the status of the
// document?"), a negation ("don't create a note"), an open ask and a quoted
// title never read as a create.
export function aiAskedCreateKinds(text) {
  const raw = String(text || '')
  if (!raw.trim()) return []
  // A quoted title names a thing; its words never ask for one.
  const plain = raw.replace(
    /"[^"\n]*"|“[^”\n]*”|„[^“”\n]*[“”]|«[^»\n]*»|(^|\s)'[^'\n]+'(?=[\s.,!?;:]|$)/g,
    (m, lead) => (lead || '') + ' '
  )
  const out = new Set()
  for (const sentence of plain.split(/[.!?;\n]+/)) {
    _enAskedKinds(sentence, out)
    _kaAskedKinds(sentence, out)
  }
  return [...out]
}

// The turn text's asked kinds, with the shell's own ask reader when it is
// registered (ROUTER's `assistantAskIntent`, functions/assistantAskRoute.js):
// a question or an open ask is never a create; a document ask is a note.
const _askedCreateKindsFor = (el, text) => {
  const own = new Set(aiAskedCreateKinds(text))
  const fns = el && el.context && el.context.functions
  if (fns && typeof fns.assistantAskIntent === 'function') {
    let intent = null
    try {
      intent = el.call('assistantAskIntent', text)
    } catch (_) {
      intent = null
    }
    if (intent === 'question' || intent === 'open') return []
    if (intent === 'document') own.add('note')
  }
  return [...own]
}

const _takeAskedCreate = (root, k) => {
  const key = `${k}|${String((root && root[k + 'currentAssistantId']) || '')}`
  const n = _askedCreatesRun.get(key) || 0
  if (n >= ASKED_CREATES_PER_TURN) return false
  _askedCreatesRun.set(key, n + 1)
  if (_askedCreatesRun.size > 50) _askedCreatesRun.delete(_askedCreatesRun.keys().next().value)
  return true
}

// Same resolution order as AiSettingsPanel's picker: the admin workspace pin
// (when the user scoped settings to the whole workspace) wins, then the
// per-user localStorage mode, then the safe default — ask.
const _resolveClientAuthMode = (root) => {
  try {
    if (root && root._aiScope === 'workspace') {
      const pin = root.workspaceSettings && root.workspaceSettings.aiProvider
      if (pin && pin.authMode) return pin.authMode
    }
  } catch (_) {}
  try {
    const ai = shellGetAiService()
    const mode = ai && ai.getAuthMode && ai.getAuthMode()
    if (mode) return mode
  } catch (_) {}
  return 'ask'
}

// The gate onToolCall routes through. `runFn` is the actual tool execution
// (bound _runPlatformTool) — injected so the gate stays pure enough to test
// without a browser. Returns runFn's promise directly whenever no ask is due.
export function runClientToolWithAuth(el, root, k, call, runFn, authModeOverride) {
  const tool = call && call.tool
  const fn = call && call.fn
  const mode = authModeOverride || _resolveClientAuthMode(root)
  // ASK-ALL: every write asks — an asked create too (D2 below never applies).
  // No settings row offers it yet; the gate honours the value when one does.
  const askAll = mode === 'askAll' || mode === 'ask-all'
  // The server's per-tool `confirm` flag (carried on the `tool.call_required`
  // frame and on the persisted pendingClientCall) is an explicit instruction to
  // ask. It OVERRIDES both Auto mode and the read-only classifier: the tool
  // registry knows things a client-side name check cannot, so a tool that only
  // LOOKS read-only must still be able to demand consent. It can only ever ADD
  // an ask — it never skips one. `forceAsk` is the client's own twin: a call a
  // loaded conversation re-offers must never run on its own (item E,
  // reofferPendingClientCall `onLoad`). A delete is forced the same way (D9,
  // isAiDeleteTool above): Auto mode never runs one without the row.
  const aiDelete = isAiDeleteTool(tool, fn)
  const forced = !!(call && (call.confirm || call.forceAsk)) || aiDelete
  // MODULE-FN-READONLY-ENFORCED-1 — an app verb the manifest marks
  // `readOnly: true` runs without the Run / Skip row when the server's frame
  // AND this shell's live stash both say so; _runPlatformTool then runs it on
  // a read-only caller that refuses every write, so skipping the ask is safe
  // even for a mislabelled verb. A server `confirm` still forces the ask.
  const readOnlyCall = !!(call && call.readOnly) && isLiveReadOnlyModuleFn(el, fn)
  if (!forced && ((mode !== 'ask' && !askAll) || readOnlyCall || isReadOnlyAiTool(tool, fn)))
    return runFn()
  // AI-307 (tickets/ai.md) — "Approve all for this run": the first approval
  // card can grant the REST of the current turn. Scope guarantee falls out of
  // keying the grant to the turn's assistant id — a new turn mints a new id,
  // so the flag can never leak forward, and it is never persisted anywhere.
  // A delete is outside the grant (D9): it asks even after "Run all" — the
  // grant answers the rows on screen, never a delete that comes later.
  if (
    !(call && call.forceAsk) &&
    !aiDelete &&
    root &&
    root._aiApproveAllFor &&
    root._aiApproveAllFor === String(root[k + 'currentAssistantId'] || '')
  ) {
    return runFn()
  }
  // D2 — a create THIS turn's own words asked for runs at once; its row then
  // reads "Created ✓ · Open · Undo". `askText` rides only the live onToolCall,
  // so a re-offered call (no turn text) keeps the ask.
  if (!forced && !askAll && call && call.askText) {
    const kind = ASKED_CREATE_FNS[fn] || ASKED_CREATE_FNS[tool]
    if (
      kind &&
      _askedCreateKindsFor(el, call.askText).includes(kind) &&
      _takeAskedCreate(root, k)
    ) {
      return runFn()
    }
  }
  const id = String((call && call.callId) || `${fn || tool}-${Date.now()}`)
  _patchToolActivity(
    root,
    { phase: 'approval', callId: id, tool: tool || fn, args: call && call.args },
    k
  )
  // Auto-expand the row — an honest ask shows WHAT it is approving.
  if (root && root.update) {
    root.update(
      { _aiToolOpen: { ...(root._aiToolOpen || {}), [id]: true } },
      { preventFetch: true }
    )
  }
  return new Promise((resolve) => {
    _pendingToolApprovals[id] = {
      root,
      k,
      tool: tool || fn,
      run: runFn,
      resolve,
      // The conversation it waits in — a new message in that thread
      // supersedes it (supersedeParkedAiToolApprovals).
      thread: (root && root[k + 'activeSessionId']) || null
    }
  })
}

// Settle EVERY parked approval as declined. A turn error, thread switch, or
// new session orphans parked approvals, and an unsettled promise strands the
// SERVER loop forever — the loop waits on a tool-result nobody can post any
// more, which surfaced as "approve did nothing until I typed continue".
// Called from the transport's own error path and from sessions.js on thread
// changes.
// AI-307 — el.call('approveAllAiToolsForTurn') from the approval card's
// "Run all" button. Two effects, both scoped to the CURRENT turn: (1) latch
// `_aiApproveAllFor` to the live turn's assistant id (the gate above lets
// every LATER effectful call of this turn run without parking; a new turn's
// new id expires it naturally, and it never persists), and (2) approve every
// ALREADY-PARKED ask so the click that granted the run also unblocks the
// card it was clicked on. `this` is the calling element (registered-fn
// contract, same as aiWorkspaceOps).
export function approveAllAiToolsForTurn() {
  const root = (this && this.state && (this.state.root || this.state)) || null
  if (!root || !root.update) return { ok: false, error: 'No workspace UI available.' }
  // The active scope = whichever surface has a live turn (same preference
  // order as _cardScope).
  const k = root.currentAssistantId ? '' : root._db_currentAssistantId ? '_db_' : '_ax_'
  const turnId = String(root[k + 'currentAssistantId'] || '')
  if (!turnId) return { ok: false, error: 'No turn is running.' }
  root.update({ _aiApproveAllFor: turnId }, { preventFetch: true })
  for (const id of Object.keys(_pendingToolApprovals)) {
    resolveAiToolApproval(id, true)
  }
  return { ok: true, turn: turnId }
}

// `opts.notRun` (a bundle key): the person never decided — the turn ended
// under the row (a failed turn). The row says so plainly, as a step that did
// not run, with the way to run it again (round 1b, sam: the row read
// "skipped", a word for the person's own choice). Without it the row keeps the
// quiet "skipped" — a new message or a stop IS the person's choice.
// → the number of approvals it settled.
export function settleParkedAiToolApprovals(reason, opts) {
  const ids = Object.keys(_pendingToolApprovals)
  for (const id of ids) {
    const held = _pendingToolApprovals[id]
    if (opts && opts.notRun) {
      expireParkedAiToolApproval(id, opts.notRun, reason)
      continue
    }
    delete _pendingToolApprovals[id]
    // AI-307 hygiene — a turn that ends by settlement also drops its
    // approve-all grant (the id-keying already makes a stale grant inert;
    // this keeps the state clean rather than merely harmless).
    if (held.root && held.root._aiApproveAllFor && held.root.update) {
      try {
        held.root.update({ _aiApproveAllFor: null }, { preventFetch: true })
      } catch (_) {}
    }
    try {
      _patchToolActivity(held.root, { phase: 'denied', callId: id, tool: held.tool }, held.k)
    } catch (_) {}
    try {
      held.resolve({
        ok: false,
        denied: true,
        error: reason || 'This action was not approved before the turn ended.'
      })
    } catch (_) {}
  }
  return ids.length
}

// B — the person wrote a NEW message while a step of this thread waited on
// Run / Skip (dev 2026-10-27, thread 6ae11be063f259960e3fa243: create_note
// waited; "Are you still doing the document?" and "How long does it take…?"
// were never answered). The old settle answered the step as DECLINED, the SDK
// posted that result, and the result POST raced the new message POST for the
// conversation's turn lock — the loser got 409, and the SDK drops a message
// that gets 409. Now nothing is posted for the step: the server closes it
// under the new message's own lock (closeDanglingClientCall, reason
// `new_message`). Its row reads "Skipped — you sent a new message" with
// "Run it now" (aiToolRunNow), never "skipped" as if the person chose it.
// The held promise resolves `superseded`; the transport's paths never hand
// such a result to the SDK. `conversationId`: the thread the new message goes
// to — a step waiting in another thread is not touched. → how many it took.
export function supersedeParkedAiToolApprovals(root, k, conversationId) {
  const scope = String(k || '')
  const conv = conversationId == null ? '' : String(conversationId)
  let count = 0
  for (const id of Object.keys(_pendingToolApprovals)) {
    const held = _pendingToolApprovals[id]
    if (!_waitsInThread(held, root, scope, conv)) continue
    delete _pendingToolApprovals[id]
    count += 1
    _boundedAdd(_supersededCalls, String(id))
    if (root && root._aiApproveAllFor && root.update) {
      try {
        root.update({ _aiApproveAllFor: null }, { preventFetch: true })
      } catch (_) {}
    }
    _patchToolRow(root, scope, id, { status: 'superseded', doneTs: Date.now() })
    try {
      // Never posted (see above): the server writes the step's result.
      held.resolve({ ok: false, superseded: true, notRun: true, reason: 'new_message' })
    } catch (_) {}
  }
  // An ask_user card waits the same way: its turn is suspended on the
  // person's pick, and the server closes that call under the new message too.
  for (const cardId of Object.keys(_pendingCardResolves)) {
    const wait = _pendingCardResolves[cardId]
    if (!_waitsInThread(wait, root, scope, conv)) continue
    delete _pendingCardResolves[cardId]
    count += 1
    try {
      wait.supersede()
    } catch (_) {}
  }
  return count
}

// One timeline row, patched in the message that holds it — a re-offered row
// sits on a restored message, not on the live turn's.
const _patchToolRow = (root, k, callId, patch) => {
  if (!root || !root.update) return null
  const id = String(callId || '')
  const list = root[k + 'chatMessages'] || []
  const msg = list.find((m) => m && Array.isArray(m.tools) && m.tools.some((t) => t && t.id === id))
  if (!msg) return null
  const tools = msg.tools.map((t) => (t && t.id === id ? { ...t, ...patch } : t))
  _shellPatchAssistantMessage(root, msg.id, { tools }, k)
  return tools.find((t) => t && t.id === id)
}

// el.call('aiToolRunNow', callId) — the "Run it now" button on a superseded
// row. The step's call is closed on the server, so it cannot be run in place:
// the button asks the AI again, as the person's own follow-up message, in
// their words ("Please run this step now: <step>") — the loop then calls the
// tool again, and the gate treats it like any other call. Once per row.
const _ROW_SCOPES = [
  ['', 'general'],
  ['_db_', 'dashboard'],
  ['_ax_', 'addapp']
]
export function aiToolRunNow(callId) {
  const el = this && typeof this.call === 'function' ? this : _creditsEl()
  const root = (el && el.state && (el.state.root || el.state)) || _shellResolveRoot(null)
  const id = String(callId || '')
  // `reason` is a code for the caller (the row's button ignores it).
  if (!el || typeof el.call !== 'function' || !root || !root.update || !id) {
    return { ok: false, reason: 'no-ui' }
  }
  for (const [k, scope] of _ROW_SCOPES) {
    const msg = (root[k + 'chatMessages'] || []).find(
      (m) => m && Array.isArray(m.tools) && m.tools.some((t) => t && t.id === id)
    )
    if (!msg) continue
    const row = msg.tools.find((t) => t && t.id === id)
    if (row.status !== 'superseded' || row.rerun) return { ok: false, reason: 'not-superseded' }
    // The step in the row's own words, in the session language: the server
    // label, else what the row itself shows (ChatToolStepLabel, the row's own
    // text factory: the client step phrase, else the generic step words).
    // Never the tool name: a Georgian page sent "…: create ticket" (wave 2,
    // dev 2026-10-28). Read at call time, never at module init.
    const shown = _wsShared.components.ChatToolStepLabel
    const phrase =
      shown && typeof shown.text === 'function'
        ? String(shown.text(el, { ...row, label: null }) || '').trim()
        : ''
    const step =
      _toolLabel(row.label) ||
      phrase ||
      String(el.call('polyglot', 'ai.toolStep.generic') || '').trim()
    const ask = String(el.call('polyglot', 'workspace.simone.approval.runNowPrompt'))
    _patchToolRow(root, k, id, { rerun: true })
    el.call(
      'sendPrompt',
      ask.replace('{step}', () => step),
      scope
    )
    return { ok: true, sent: true }
  }
  return { ok: false, reason: 'not-found' }
}

// A parked approval that can no longer run — the turn ended under it, or the
// server's own wait for the person ended (round 1b, sam: "skipped 46.0s" while
// he tabbed to it). The row becomes a step that did not run, its reason in
// plain words (`key`, a bundle key — ChatToolRow shows a failed step's reason
// under it), and the answer it sits on offers to run it again: one chip that
// sends that request as the person's message (the loop then asks again). The
// held promise settles as not run, so nothing waits on it.
const expireParkedAiToolApproval = (id, key, reason) => {
  const held = _pendingToolApprovals[id]
  if (!held) return
  delete _pendingToolApprovals[id]
  const root = held.root
  if (root && root._aiApproveAllFor && root.update) {
    try {
      root.update({ _aiApproveAllFor: null }, { preventFetch: true })
    } catch (_) {}
  }
  const failure = _sayKey(key || 'workspace.simone.approval.expired')
  try {
    _patchToolActivity(root, { phase: 'result', callId: id, tool: held.tool, failure }, held.k)
    _patchToolActivity(
      root,
      {
        phase: 'completed',
        callId: id,
        tool: held.tool,
        ok: false,
        notRun: true,
        summary: failure
      },
      held.k
    )
  } catch (_) {}
  try {
    const list = (root && root[held.k + 'chatMessages']) || []
    const msg = list.find(
      (m) => m && Array.isArray(m.tools) && m.tools.some((t) => t && t.id === id)
    )
    if (msg) {
      // ONE chip, in the shape the shared ChatMessage reads: its words
      // (`label`) and the request a press sends (`prompt`). A bare string
      // spread into character keys there: a chip with no words that did
      // nothing.
      const again = _sayKey('workspace.simone.approval.runAgain')
      const had = Array.isArray(msg.suggestions) ? msg.suggestions : []
      if (!had.some((s) => s && s.label === again)) {
        _shellPatchAssistantMessage(
          root,
          msg.id,
          { suggestions: had.concat({ label: again, prompt: again }) },
          held.k
        )
      }
    }
  } catch (_) {}
  try {
    held.resolve({
      ok: false,
      denied: true,
      notRun: true,
      error: reason || 'This action was not approved in time — it did not run.'
    })
  } catch (_) {}
}

// el.call('resolveAiToolApproval', callId, approved) — fired by ChatToolRow's
// Run / Skip buttons. Approve runs the held tool and settles the loop with its
// real result; deny settles { ok:false, denied:true } and the row keeps a
// quiet "skipped" state.
export function resolveAiToolApproval(callId, approved) {
  const held = _pendingToolApprovals[callId]
  if (!held) return
  delete _pendingToolApprovals[callId]
  const { root, k, tool, run, resolve } = held
  // A detached turn that stopped following while this row waited picks the
  // follow up again (the answer settles the call; the SDK posts the result).
  const resumeFollow = () => {
    if (typeof held.onAnswered === 'function') {
      try {
        held.onAnswered()
      } catch (_) {}
    }
  }
  if (!approved) {
    _patchToolActivity(root, { phase: 'denied', callId, tool }, k)
    resolve({ ok: false, denied: true, error: 'The user declined this action.' })
    resumeFollow()
    return
  }
  _patchToolActivity(root, { phase: 'approved', callId, tool }, k)
  Promise.resolve()
    .then(run)
    .then(
      (res) => resolve(res),
      (err) => resolve({ ok: false, error: (err && err.message) || String(err) })
    )
    .then(resumeFollow)
}

// ── Re-offering a pending client call after a reload ────────────────────────
// SIMONE-APPROVAL-REOFFER-PENDING-CLIENT-CALL-UI-1. The gate above parks its
// promise in memory, so a reload kills it and the suspended server turn waits
// on a tool-result nobody can post. The server now persists the whole call on
// the conversation and serves it on the ordinary read; `sessions.js`
// restoreActiveThread picks it up and calls this.
//
// Deliberately NOT a second authorization policy: the call goes back through
// the SAME `runClientToolWithAuth` gate the live turn uses, so "Ask first"
// re-asks with the fn + args on the row the restore just painted, while
// read-only / navigation / render tools and Auto mode resume straight through
// — one rule, in one place. The server's own `confirm` flag only ever ADDS an
// ask (it forces the gate's ask mode); it can never skip one.
// How an ACCEPTED resume is followed (turnSettledAfter): one ordinary
// conversation read every 2 s, for at most 2 minutes, and only while the
// person still has the thread open.
const RESUME_FOLLOW_INTERVAL_MS = 2000
const RESUME_FOLLOW_TIMEOUT_MS = 120000

export function reofferPendingClientCall(payload) {
  const { offer, conversationId, scope, opts, stateRoot } = payload || {}
  const call = normalizePendingClientCall(offer)
  if (!call || !conversationId) {
    return Promise.resolve({ ok: false, error: 'No completable pending call.' })
  }
  const root = _shellResolveRoot({ stateRoot })
  if (!root) return Promise.resolve({ ok: false, error: 'No workspace UI available.' })
  const k = scope === 'addapp' ? '_ax_' : scope === 'dashboard' ? '_db_' : ''
  const callEl = (typeof window !== 'undefined' && window.__wsRoot) || _installerEl || this
  // The CALLER's service wins: sessions.js just used it to read this very
  // conversation, and `el.sdk.ai` is a different stub without the conversation
  // API (see sessions.js `_ai`). Fall back to the shared singleton only when
  // the caller had none to pass.
  const ai = (payload && payload.ai) || shellGetAiService()
  if (!ai || typeof ai.submitToolResult !== 'function') {
    return Promise.resolve({ ok: false, error: 'No AI service to resume the turn.' })
  }

  const typing = (on) => {
    try {
      if (root.update) root.update({ [k + 'chatTyping']: !!on })
    } catch (_) {}
  }

  // E — a load never runs a step again by itself (ON_LOAD_ASK_FNS above). A
  // step still waiting on its Run / Skip, or one this page received live and
  // did not run, is the live path's. `payload.live`: the live turn hands over
  // a call it never received (_followAfterDrop) — the person is there.
  const cid = String(call.callId)
  if (_pendingToolApprovals[cid]) return Promise.resolve({ ok: false, skipped: 'waiting' })
  const ranHere = _ranHere(root, cid)
  if (!ranHere && _callsOf(_seenOnRoot, root).has(cid)) {
    return Promise.resolve({ ok: false, skipped: 'seen' })
  }
  const onLoad = !(payload && payload.live)
  let first = true

  return resumeFromPendingClientCall({
    offer: call,
    runWithAuth: (c) => {
      const isFirst = first
      first = false
      // Restore the row to "running" first: history rebuilds a bare tool_call
      // as a finished row, and an offer that is still waiting must not read as
      // already done. The gate flips it to `approval` when it parks. No `args`
      // on purpose — the restored row already holds the server's named-args
      // object, which reads better than the positional array.
      _patchToolActivity(root, { phase: 'started', callId: c.callId, tool: c.tool }, k)
      // The loaded step: asked, never run by itself, when it moves or covers
      // the screen or ran in this tab before a reload. Steps chained after it
      // are live.
      const forceAsk =
        isFirst &&
        onLoad &&
        (ON_LOAD_ASK_FNS.has(c.fn) || _ranBeforeReload(conversationId, c.callId))
      const gate = () =>
        runClientToolWithAuth(callEl, root, k, forceAsk ? { ...c, forceAsk: true } : c, () =>
          _rememberRun(
            root,
            conversationId,
            c,
            _runPlatformTool(callEl, c.fn, c.args, {
              root,
              k,
              readOnly: !!c.readOnly,
              // a question asked again draws under its own step
              callId: c.callId
            })
          )
        )
      // An APP verb is gated (read-only or Run / Skip) and run from the module
      // stash, and a cold load restores the thread BEFORE the apps publish it
      // (MODULE-FN-READONLY-LABELS-LAG-AN-APP-PUSH-1): decided then, a
      // read-only verb parked on Run / Skip and a run read "not permitted".
      // Decide once the stash declares the verb or the apps pass settled.
      // A step this page already ran posts that run's result instead (E.1).
      const ran = isFirst ? ranHere : null
      return (
        ran
          ? Promise.resolve(ran)
          : moduleStashReady(callEl, root, c.fn)
            ? gate()
            : waitForModuleStash(callEl, root, c.fn).then(gate)
      ).then((res) => {
        // Superseded by a new message (supersedeParkedAiToolApprovals): its
        // row already says so, and nothing is posted.
        if (res && res.superseded) return res
        // Only NOW is the turn actually working again — parking on Run / Skip
        // is the user's time, not the server's, and "Thinking…" would lie.
        typing(true)
        const created = toolOutcome(res)
        const failure = toolFailure(res)
        if (failure)
          _patchToolActivity(root, { phase: 'result', callId: c.callId, tool: c.tool, failure }, k)
        _patchToolActivity(
          root,
          {
            phase: 'completed',
            callId: c.callId,
            tool: c.tool,
            ok: res && res.ok !== false,
            summary:
              (res && (res.summary || (typeof res.error === 'string' ? res.error : ''))) || '',
            ...(created ? { created } : {})
          },
          k
        )
        return res
      })
    },
    submitToolResult: (callId, result) => ai.submitToolResult(conversationId, callId, result, opts),
    // The server accepted the result and runs on past its ack window (202):
    // its answer streams to a page that is not listening. Follow the ordinary
    // read until the turn lands, then paint the thread from the server's own
    // transcript — the same rows a reload would show.
    awaitTurn: async (callId) => {
      if (typeof ai.getConversation !== 'function') return null
      const every = (payload && payload.followIntervalMs) || RESUME_FOLLOW_INTERVAL_MS
      const until = Date.now() + ((payload && payload.followTimeoutMs) || RESUME_FOLLOW_TIMEOUT_MS)
      const open = () => root[k + 'activeSessionId'] === conversationId
      while (Date.now() < until && open()) {
        await new Promise((resolve) => setTimeout(resolve, every))
        let data
        try {
          data = await ai.getConversation(conversationId, opts)
        } catch (_) {
          continue
        }
        const settled = turnSettledAfter(data, callId)
        if (!settled) continue
        if (settled.done && open()) {
          try {
            const words = { continueWord: _continueWord(callEl) }
            const rows = _creditsRows(_toChatMessages(data.messages, words), root, callEl)
            root.update({ [k + 'chatMessages']: rows })
          } catch (_) {
            /* best-effort — the turn finished; a reload paints it */
          }
        }
        return settled
      }
      return null
    },
    onAnswer: (message) => {
      const text = _messageText(message)
      if (!text) return
      // A resumed turn can end in the credit refusal too: the shell's sentence
      // and the metadata its notification and banner read (and only then —
      // no other message carries its metadata onto this row).
      const refused = _creditsRow(text, message.metadata, root, callEl)
      const next = (root[k + 'chatMessages'] || []).concat({
        id: String(message.id || message._id || `a-${Date.now()}`),
        role: 'assistant',
        text: refused ? refused.text : text,
        ts: Date.now(),
        streaming: false,
        tools: [],
        cards: [],
        suggestions: Array.isArray(message.metadata?.suggestions)
          ? message.metadata.suggestions
          : [],
        ...(refused ? { metadata: refused.metadata } : {})
      })
      root.update({ [k + 'chatMessages']: next })
    }
  }).then((res) => {
    // A superseded step belongs to a thread the new message now drives — its
    // typing flag is that turn's.
    if (res && res.superseded) return res
    typing(false)
    try {
      if (callEl && typeof callEl.call === 'function') {
        callEl.call('refreshSessionsAfterTurn', scope)
      }
    } catch (_) {
      /* best-effort — history just won't refresh until reload */
    }
    return res
  })
}

// Per-turn flag — set true when the cloud loop drives injectPageInApp itself
// (so the onDone build-spec render fallback knows a render already happened and
// skips, avoiding a double-render). Reset at the start of every dispatch turn.
let _extensionRenderedThisTurn = false

// ── Client (browser) tools ───────────────────────────────────────────────────
// Defense-in-depth allow-list mirroring the server's client-tool catalog
// (workspaceTools.js): only these platform functions may be driven by the AI.
// Destructive ones (logout, switchOrg, …) are never here. The server already
// resolved tool → fn + positional args; we re-check fn here and shape the
// result the model reads. Each returns a structured { ok, … } object.
const CLIENT_TOOL_FNS = new Set([
  'openAppPage', // navigate_to_app
  'openPageInSplit', // open_page_in_split — page beside the / dashboard thread
  'renderAnswerUi', // render_answer_ui — transient DOMQL answer view in the PageSlot
  'changeGlobalTheme', // set_theme
  'globalSearch', // open_search
  'confirmDialog', // confirm_with_user
  'closeAllModals', // close_modals
  'injectPageInApp', // render_extension_page
  'renderMultiPageApp', // create_app — render a multi-page app in the add-app pane
  'selectPageItem', // select_page_item
  'renderCards', // render_cards — attach display cards to the message
  'askUser', // ask_user — render choice/confirm buttons + suspend until clicked
  'aiDescribeCapabilities', // describe_capabilities — the context's capability card
  'aiStartTour' // start_tour — the quick tour over the member's screen (aiStartTour.js)
])

// AI workspace-operation tools (functions/aiWorkspaceOps.js): the assistant
// performs what a user can do in the UI (create/rename/duplicate/delete files,
// notes, tickets, channels…). Each takes flat args, resolves the target by
// id/name in the unified root state, reuses the existing UI function + SDK route,
// and returns a structured { ok, … }. Dispatched generically (no per-tool case).
const DATA_OP_FNS = new Set([
  'aiCreateFolder',
  'aiRenameFile',
  'aiDeleteFile',
  'aiDuplicateFile',
  'aiMoveFileToFolder',
  'aiCreateNote',
  'aiRenameNote',
  'aiDeleteNote',
  'aiCreateTicket',
  'aiUpdateTicket',
  'aiAssignTicket',
  'aiMoveTicket',
  'aiDeleteTicket',
  'aiCreateChannel',
  'aiSendMessage',
  // AI-CREATED home widgets (tickets/opus.md "Per-workspace AI-CREATED
  // widgets"). Listed here — NOT in READ_ONLY_AI_TOOLS — so all three are
  // classified WRITE explicitly rather than relying on the unknown-tool
  // default, and every one of them goes through runClientToolWithAuth →
  // resolveAiToolApproval exactly like every other write action (Ask-first
  // mode shows a Run / Skip row carrying a live preview of the pending
  // widget — see ChatToolRow). `aiEditWidget` would otherwise be caught by
  // NEITHER read prefix, and `aiCreateWidget`/`aiDeleteWidget` are writes by
  // any reading — the point of naming them is that the classification is a
  // decision here, not an accident of a regex.
  'aiCreateWidget',
  'aiEditWidget',
  'aiDeleteWidget',
  // mail_draft_reply (architecture/MAIL.md §3.2a) — opens the compose dock
  // through THE one entry point (openMailCompose, mail's eager slice) with a
  // prefilled draft; it never sends. Classified WRITE (not in any read
  // prefix) on purpose: Ask-first mode shows Run/Skip before a dock pops
  // over the person's screen. The server-side tool definition ships with
  // MAIL-AI-TOOLS-1; until then no dispatch names this fn.
  'aiMailDraftReply'
  // NO module fns here — this static list is house tools only. Module-declared
  // fns (v2 manifests AND the legacy v1 module, whose normalizer stashes
  // aiFunctions = V1_AI_FNS — workspaceProjects.js _normalizeManifest) are
  // accepted at CALL time by the dynamic module gate below (_moduleAiFns), so
  // they're honored only while their module is live and revoked on teardown.
])

// AI fns declared by the ACTIVE workspace module — read at CALL time from the
// module stash (root.workspaceProject.aiFunctions, written by
// loadWorkspaceProject) so a module injected after transport install is
// honored, and revoked the moment the module tears down. Set-ified per array
// identity (WeakMap) so the per-call gate stays O(1) without re-allocating.
const _EMPTY_FN_SET = new Set()
const _moduleAiFnSetCache = new WeakMap()
const _moduleAiFns = (el) => {
  const root = el?.state?.root || el?.getRoot?.()?.state?.root
  const list = root?.workspaceProject?.aiFunctions
  if (!Array.isArray(list) || !list.length) return _EMPTY_FN_SET
  let set = _moduleAiFnSetCache.get(list)
  if (!set) {
    set = new Set(list)
    _moduleAiFnSetCache.set(list, set)
  }
  return set
}

// The verbs the ACTIVE apps run read-only (root.workspaceProject
// .readOnlyAiFunctions — the composed stash; MODULE-FN-READONLY-ENFORCED-1),
// read at call time like _moduleAiFns: a verb counts only while its module is
// live AND the stash marks it.
export function isLiveReadOnlyModuleFn(el, fn) {
  if (!fn || !_moduleAiFns(el).has(fn)) return false
  const root = el?.state?.root || el?.getRoot?.()?.state?.root
  const list = root?.workspaceProject?.readOnlyAiFunctions
  return Array.isArray(list) && list.includes(fn)
}

// Can a call to `fn` be decided NOW? The shell's own tools always can; an app
// verb once the module stash declares it, or once the apps pass has settled
// (`_appRoutesSettled` lands in the same update as the composed stash) — then
// an absent verb is genuinely absent and the gate answers that honestly.
export function moduleStashReady(el, root, fn) {
  if (!fn || CLIENT_TOOL_FNS.has(fn) || DATA_OP_FNS.has(fn)) return true
  if (_moduleAiFns(el).has(fn)) return true
  return !!(root && root._appRoutesSettled)
}

// Wait — bounded — until moduleStashReady. Resolves true when ready, false on
// the timeout (the gate then decides on what it has, as before).
export function waitForModuleStash(el, root, fn, { timeoutMs = 15000, intervalMs = 200 } = {}) {
  return new Promise((resolve) => {
    if (moduleStashReady(el, root, fn)) return resolve(true)
    const started = Date.now()
    const tick = () => {
      if (moduleStashReady(el, root, fn)) return resolve(true)
      if (Date.now() - started >= timeoutMs) return resolve(false)
      setTimeout(tick, intervalMs)
    }
    setTimeout(tick, intervalMs)
  })
}

// `scope` — the DISPATCHING turn's { root, k }, threaded from onToolCall so
// chat-surface tools attach to the exact thread that asked. Callers without a
// live dispatch (build-spec fallback, tests) omit it and the chat cases fall
// back to the _cardScope heuristic.
async function _runPlatformTool(el, fn, args = [], scope = null) {
  if (!el || typeof el.call !== 'function') {
    return { ok: false, error: 'No workspace UI is available to run this action.' }
  }
  // Third acceptance leg: fns the ACTIVE workspace module declares AI-drivable
  // (root.workspaceProject.aiFunctions). Scoped exactly like the module's
  // injected functions — gone the moment the module unloads.
  const isModuleFn = _moduleAiFns(el).has(fn)
  if (!CLIENT_TOOL_FNS.has(fn) && !DATA_OP_FNS.has(fn) && !isModuleFn) {
    return { ok: false, error: `Platform function "${fn}" is not permitted from the AI.` }
  }
  try {
    // Data-operation tools (and module-declared fns) take flat args and return
    // their own { ok, … }.
    if (DATA_OP_FNS.has(fn) || isModuleFn) {
      // A verb the frame and the live stash both mark read-only runs on a
      // read-only caller (moduleReadOnlyCall.js): records reads pass, every
      // write / action / send from inside it is refused, and a refused write
      // turns the result into ok:false naming it.
      if (isModuleFn && scope && scope.readOnly && isLiveReadOnlyModuleFn(el, fn)) {
        const caller = readOnlyCaller(el, { fn, refuse: [...DATA_OP_FNS] })
        let res
        try {
          res = await caller.call(fn, ...args)
        } catch (e) {
          // A refused write usually surfaces as the verb's own throw — the
          // record still names what was refused.
          res = { ok: false, error: (e && e.message) || 'The function failed.' }
        }
        return withReadOnlyOutcome(
          res && typeof res === 'object' ? res : { ok: res !== false, result: res },
          readOnlyCallOf(caller)
        )
      }
      const res = await el.call(fn, ...args)
      return res && typeof res === 'object' ? res : { ok: res !== false, result: res }
    }
    switch (fn) {
      case 'openAppPage':
        el.call('openAppPage', args[0])
        return { ok: true, navigated: args[0] }
      case 'openPageInSplit': {
        // open_page_in_split — show a workspace page BESIDE the conversation in
        // the shell PageSlot (root._canvasSlot), the same sheet every app page
        // opens in. The docked assistant sits beside it (content shrinks), so it
        // behaves identically on home and every other route now that the inline
        // dashboard thread is retired. args[0] = route ('' closes it).
        const route = String(args[0] || '').trim()
        const callRoot = el?.state?.root || el?.getRoot?.()?.state?.root
        if (callRoot?.update) {
          callRoot.update(
            route
              ? { _canvasSlot: route, _slotFullscreen: null }
              : { _canvasSlot: null, _canvasSlotSecond: null, _slotFullscreen: null }
          )
        }
        return route ? { ok: true, opened: route } : { ok: true, closed: true }
      }
      case 'renderAnswerUi': {
        // render_answer_ui — draw a transient DOMQL answer view
        // (canvas/functions/answerUi.js owns the parse + audit + register).
        // INLINE in the thread (AI_ASSISTANT_SPEC v1 §3): the view also rides
        // the answer as a 'view' card, so the sidebar AND the main thread draw
        // it where the question was asked (components/AssistantThreadBlock.js).
        // The split pane beside the conversation opens as before — except while
        // the MAIN thread holds the home (the view is already in the thread;
        // its "Open in split" is one click away).
        const root = scope?.root || el.state?.root || el.state
        const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
        // THE BUILDER'S OWN TURN: a drawn view IS the app being built, so it
        // renders in the builder's Preview pane — its render host — never in
        // a split pane beside the builder (aiko, tomas round 1b: the Preview
        // said "Your idea is taking shape" all session while the board showed
        // in a floating pane). The same guarded path as render_extension_page
        // (the host builds through buildInlineExtensionDef: the audit and the
        // containment gate), so Save keeps it; a refusal returns to the AI as
        // it came, and it rebuilds.
        const builderHost = (typeof window !== 'undefined' && window.__addAppHost) || null
        if (k === '_ax_' && builderHost && typeof builderHost.call === 'function') {
          const view = args[0] || {}
          const shown = await builderHost.call('injectPageInApp', {
            name: String(view.title || '').trim() || _sayKey('workspace.apps.nameFallback'),
            source: String(view.source || ''),
            entities: []
          })
          if (shown && shown.ok !== false) {
            _extensionRenderedThisTurn = true
            return {
              ...(shown && typeof shown === 'object' ? shown : {}),
              ok: true,
              rendered: true,
              note: "Shown in the builder's Preview beside the conversation. The person can Save it as an app there."
            }
          }
          return shown && typeof shown === 'object' ? shown : { ok: false }
        }
        const inMain = !!(root && root._homeThreadOpen === true && root.assistantDock === 'main')
        const res = await el.call('renderAnswerUi', {
          ...(args[0] || {}),
          ...(inMain ? { open: false } : {})
        })
        if (res && res.ok && res.opened) {
          try {
            _attachCards(root, [{ type: 'view', route: res.opened, title: res.title || '' }], k)
          } catch (_) {
            /* no thread to attach to (a build-spec fallback) — the view stays registered */
          }
        }
        return res && typeof res === 'object' ? res : { ok: true }
      }
      case 'changeGlobalTheme':
        el.call('changeGlobalTheme', args[0])
        return { ok: true, theme: args[0] }
      case 'closeAllModals':
        el.call('closeAllModals')
        return { ok: true }
      case 'injectPageInApp': {
        // render_extension_page — args[0] = { name, source, projectId }. CRITICAL:
        // render ONLY into the add-app surface's own render host
        // (window.__addAppHost), whose element carries the add-app router/context.
        // Calling injectPageInApp from the SHELL root (window.__wsRoot) walks up to
        // the shell router and hijacks the whole workspace (blank view) — never do
        // that.
        const payload = args[0] || {}
        // BIND the builder to the just-created extension's Project: the chat
        // thread becomes project-scoped from here, the sidebar marks it active,
        // and a later select reloads ITS thread. Done even when the render host
        // isn't mounted, so the binding survives a build kicked off elsewhere.
        const root = (el.state && (el.state.root || el.state)) || null
        if (payload.projectId && root?.update) {
          try {
            // A BIND, not a switch: the live conversation built this app, so
            // the chat already holds its thread. Marked hydrated BEFORE the id
            // lands — the builder's hydrateAddAppThread (subscribed to
            // _addAppProjectId) otherwise opened the app's own thread over the
            // live turn: the chat emptied to "What would you like to build?"
            // and the closing message came back only after a reload (nino,
            // round 1b). A fresh builder mount clears the mark (BuildCanvas).
            root.__addAppHydratedThreadId = String(payload.projectId)
            root.update({ _addAppProjectId: String(payload.projectId), _addAppRendered: false })
            // Refresh the apps sidebar so the new extension appears + shows active.
            el.call('loadAddAppExtensions', root)
          } catch (_) {
            /* binding is best-effort */
          }
        }
        const host = (typeof window !== 'undefined' && window.__addAppHost) || null
        if (!host || typeof host.call !== 'function') {
          // The add-app render iframe isn't mounted (the user isn't on /add-app,
          // or it hasn't booted). Queue the render so AppRender.onCreate flushes
          // it when the host installs — AND open a live preview in the shell
          // PageSlot right now, so a build is ALWAYS visible where it was asked
          // for (2026-07-24 per Nika "when it builds app it should also always
          // open preview"). The preview renders through the same guarded iframe
          // pipeline (openExtensionPreview → ExtensionEmbed).
          try {
            if (typeof window !== 'undefined') {
              window.__addAppPendingRender = {
                name: payload.name,
                source: payload.source,
                entities: payload.entities || payload.dataEntities || []
              }
            }
          } catch (_) {}
          let previewed = false
          const inThread = _previewInThread(root)
          try {
            const pv = el.call('openExtensionPreview', {
              name: payload.name,
              source: payload.source,
              entities: payload.entities || payload.dataEntities || [],
              projectId: payload.projectId || payload.extensionId || null,
              ...(inThread ? { open: false } : {})
            })
            previewed = !!(pv && pv.ok)
            if (inThread) _attachPreviewCard(scope?.root || root, scope, pv, payload.name)
          } catch (err) {
            console.warn('[simone] extension preview failed:', err?.message || err)
          }
          if (previewed) _extensionRenderedThisTurn = true
          return {
            ok: true,
            rendered: previewed,
            bound: !!payload.projectId,
            note: previewed
              ? inThread
                ? 'Preview shown in the conversation, with "Save as app" on it. The app is saved as a draft — it is not installed until the person saves it as an app.'
                : 'Preview opened beside the conversation. The app is saved — the Add App surface can iterate on it.'
              : 'Extension created and saved. Open the Add App surface to view it render.'
          }
        }
        const res = await host.call('injectPageInApp', {
          name: payload.name,
          source: payload.source,
          // Forward the declared data surface so renderExtensionInPane scopes
          // the iframe's SDK executor to exactly these workspaceProject.* entities.
          entities: payload.entities || payload.dataEntities || []
        })
        // Mark this turn as already-rendered so the onDone build-spec fallback
        // doesn't re-render the same extension.
        if (res && res.ok !== false) _extensionRenderedThisTurn = true
        return res && typeof res === 'object' ? res : { ok: true }
      }
      case 'renderMultiPageApp': {
        // create_app clientAfterExecute — args[0] = { name, pages, theme,
        // projectId }. Render ONLY into the add-app host (never the shell root —
        // same hijack hazard as injectPageInApp). Mirrors that path exactly.
        const payload = args[0] || {}
        const root = (el.state && (el.state.root || el.state)) || null
        if (payload.projectId && root?.update) {
          try {
            // A bind, not a switch — see injectPageInApp above.
            root.__addAppHydratedThreadId = String(payload.projectId)
            root.update({ _addAppProjectId: String(payload.projectId), _addAppRendered: false })
            el.call('loadAddAppExtensions', root)
          } catch (_) {
            /* binding is best-effort */
          }
        }
        const host = (typeof window !== 'undefined' && window.__addAppHost) || null
        if (!host || typeof host.call !== 'function') {
          // Host not mounted (user isn't on /add-app) — queue for AppRender's
          // onCreate flush AND open a live PageSlot preview of the app's pages
          // right here (same always-visible-build contract as injectPageInApp).
          try {
            if (typeof window !== 'undefined') {
              window.__addAppPendingApp = {
                name: payload.name,
                pages: payload.pages || [],
                theme: payload.theme || null
              }
            }
          } catch (_) {}
          let previewed = false
          const inThread = _previewInThread(root)
          try {
            const pv = el.call('openExtensionPreview', {
              name: payload.name,
              pages: payload.pages || [],
              theme: payload.theme || null,
              projectId: payload.projectId || null,
              ...(inThread ? { open: false } : {})
            })
            previewed = !!(pv && pv.ok)
            if (inThread) _attachPreviewCard(scope?.root || root, scope, pv, payload.name)
          } catch (err) {
            console.warn('[simone] app preview failed:', err?.message || err)
          }
          if (previewed) _extensionRenderedThisTurn = true
          return {
            ok: true,
            rendered: previewed,
            bound: !!payload.projectId,
            note: previewed
              ? inThread
                ? 'Preview shown in the conversation, with "Save as app" on it. The app is saved as a draft — it is not installed until the person saves it as an app.'
                : 'Preview opened beside the conversation. The app is saved — the Add App surface can iterate on it.'
              : 'App created and saved. Open the Add App surface to view it render.'
          }
        }
        const res = await host.call('renderMultiPageApp', {
          name: payload.name,
          pages: payload.pages || [],
          theme: payload.theme || null
        })
        if (res && res.ok !== false) _extensionRenderedThisTurn = true
        return res && typeof res === 'object' ? res : { ok: true }
      }
      case 'globalSearch': {
        const results = await el.call('globalSearch', args[0])
        return { ok: true, query: args[0], results }
      }
      case 'selectPageItem': {
        // select_page_item — open/select a ticket/channel/document/event/room
        // on the page the user is viewing, or a workspace record on its app's
        // page (type 'record': the third argument is its collection, sent only
        // by a server that saw `capabilities.openRecord`). Pure client
        // selection state; the fn returns its own structured { ok, … }. An
        // older server's two arguments pass through as they are.
        const res = await el.call('selectPageItem', ...args.slice(0, 3))
        return res && typeof res === 'object' ? res : { ok: true }
      }
      case 'renderCards': {
        // render_cards — attach display cards (tickets/people/thumbnails) to the
        // assistant's current message. Non-suspending; the AI keeps answering.
        // The dispatching turn's { root, k } win over the _cardScope heuristic —
        // a suspended/orphaned turn in another scope must never steal the cards.
        const root = scope?.root || el.state?.root || el.state
        const cards = Array.isArray(args[0]) ? args[0] : []
        const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
        _attachCards(root, cards, k)
        return { ok: true, rendered: cards.length }
      }
      case 'aiStartTour': {
        // start_tour — the quick tour over the member's screen
        // (functions/aiStartTour.js): home first, then the stops. Its own
        // { ok, started, stops } is what the model reads.
        const res = await el.call('aiStartTour')
        return res && typeof res === 'object' ? res : { ok: res !== false, result: res }
      }
      case 'aiDescribeCapabilities': {
        // describe_capabilities — a TYPED "what can you do here?" gets the
        // SAME catalogue card the composer's row shows: aiCapabilities.js (the
        // composer's pure builder over the chosen context — Ask / Do / Build
        // / Inspect from the installed apps' own manifests) attached to the
        // live answer, and a compact reading handed back so the model adds
        // one line instead of listing the catalogue in prose.
        const root = scope?.root || el.state?.root || el.state
        const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
        const cat = el.call('aiCapabilities', root)
        if (!cat || !Array.isArray(cat.groups) || !cat.groups.length) {
          return { ok: false, error: 'No capability catalogue for this context.' }
        }
        _attachCards(
          root,
          [{ type: 'capabilities', title: cat.title, scope: cat.scope, groups: cat.groups }],
          k
        )
        return {
          ok: true,
          shown: 'the capabilities card',
          scope: cat.scope,
          groups: cat.groups.map((g) => ({
            key: g.key,
            title: g.title,
            items: (Array.isArray(g.items) ? g.items : [])
              .map((it) => it && it.label)
              .filter(Boolean)
          }))
        }
      }
      case 'askUser': {
        // ask_user — render a choice/confirm card and SUSPEND until the user
        // clicks a button (resolveChatCard resolves this Promise → the turn
        // resumes with the pick). args = [prompt, options[], kind].
        const root = scope?.root || el.state?.root || el.state
        const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
        const prompt = args[0]
        const options = Array.isArray(args[1]) ? args[1] : []
        const kind = args[2] === 'confirm' ? 'confirm' : 'choice'
        const card =
          kind === 'confirm'
            ? {
                type: 'confirm',
                prompt,
                confirmLabel: options[0] && options[0].label,
                cancelLabel: options[1] && options[1].label
              }
            : { type: 'choice', prompt, options }
        return await _askOnCard(root, k, card, scope, (value, label, extra) =>
          _askAnswer(kind, value, label, extra)
        )
      }
      // confirm_with_user — the SAME confirm card, in the thread that asked
      // (Nika 2026-10-28: the confirmation card in the AI chat). It opened the
      // shell's modal, whose template reads `message` and dropped the tool's
      // `body`: the person read a stock line instead of the question. The
      // body IS the question; the model reads `confirmed` back (and `edit`).
      case 'confirmDialog': {
        const root = scope?.root || el.state?.root || el.state
        const k = scope && typeof scope.k === 'string' ? scope.k : _cardScope(root)
        const opts = args[0] && typeof args[0] === 'object' ? args[0] : {}
        const card = {
          type: 'confirm',
          prompt: String(opts.body || opts.message || opts.title || ''),
          confirmLabel: opts.confirmText,
          cancelLabel: opts.cancelText
        }
        return await _askOnCard(root, k, card, scope, (value, label, extra) =>
          _confirmAnswer(value, extra)
        )
      }
      default:
        return { ok: false, error: `Unhandled platform function "${fn}".` }
    }
  } catch (err) {
    return { ok: false, error: String(err?.message || err) }
  }
}
